<template>
<span v-if="isTypePpaAppraisal">
    <span v-if="!isApprovedPpaConfirmationActive">
      <span v-if="hasApprovePpaAccess && canShowPpaAction" :id="instanceId" :name="instanceId">
        <div class="quick-action" v-if="!isApprovedPpaConfirmationActive && !showStatusIcon">
          <button class="button" @click="onRequestApprovePpa()" v-tooltip="applicableIconTitle" v-if="!showStatusIcon">
            <b-icon pack="fal" icon="check-circle" custom-size="fa-lg" :class="formatIconByStatus"></b-icon>
          </button>
        </div>
        <span v-if="showStatusIcon && isApprovable" @click.stop.prevent="onRequestApprovePpa()">
          <i class="grid-icon fa-fw fal fa-check-circle fa-lg clickable has-text-danger" v-tooltip="applicableIconTitle"/>
        </span>
        <span v-if="showStatusIcon && !isApprovable" @click.stop.prevent="onRequestApprovePpa()">
          <i class="grid-icon fa-fw fal fa-check-circle fa-lg clickable" :class="formatIconByStatus" v-tooltip="applicableIconTitle"/>
        </span>
      </span>
    </span>
    <portal to="global-modal-portal" v-else :disabled="!usePortal" :order="1">
      <b-modal
        :key="'apa' + instanceId"
        :active.sync="isApprovedPpaConfirmationActive"
        :full-screen="true"
        scroll="keep"
        :has-modal-card="true"
        :canCancel="false"
        class="modal-fullscreen">
        <div class="modal-card">
          <header class="modal-card-head">
            <div class="columns" style="width: 100%">
              <div class="column is-4 is-pulled-left">
                <span class="modal-card-title">{{modalTitle}}</span>
              </div>
              <div :class="'column ' + isIndividual ? 'is-4' : 'is-8'">
                <div class="tags are-medium is-pulled-right">
                  <a class="tag is-primary" @click.stop.prevent="onStockNoClick(inventoryItem)">
                    {{ stockNo }}
                  </a>
                  <span class="tag is-primary">{{ inventoryDetails }}</span>
                  <span class="tag is-primary">{{ inventoryItem.vinNo }}</span>
                </div>
              </div>
            </div>
          </header>
          <section class="modal-card-body" v-if="isLoading">
            <b-loading :is-full-page="false" :active.sync="isLoading" :canCancel="false"></b-loading>
          </section>
          <section class="modal-card-body" v-else>
            <error-display-component :serverErrors="serverErrors" :serverErrorMessage="serverErrorMessage"></error-display-component>
            <carfax-process-status-header :vinNo="currentInventory.vinNo" :inventoryId="currentInventory.id" :messageStyling="'max-width: 240px !important;'" :leftMargin="'margin-left: 15px !important;'" :carfaxRequested="carfaxRequested" :hideHeader="hideCarfaxStatusHeader"></carfax-process-status-header>
            <form @submit.prevent="validateBeforeSubmit" v-if="!approvedSuccessfully">
              <div class="columns">
                <div class="column is-6">
                  <div class="columns is-multiline">
                    <div class="column is-12">
                      <!--⚠ carfax-dismiss goes to closeMakeModelNameInformationModal, NOT to
                           closeCarfaxMakeModelInformation. The latter exists only on the inventory
                           mixin, which this component does not mix in - so the inline prompt's Cancel
                           called nothing and the modal stayed on screen with no way out but Apply.
                           Found in the browser, 20 Sep. This screen's own handler also clears
                           showCarfaxFieldDifferences and requests the customer sync, neither of which
                           the mixin's version knows about. -->
                      <make-model-selector
                        data-vv-as="selected vehicle"
                        name="selectedMakeModel"
                        v-validate="'required'"
                        :forceColorSelection="false"
                        v-model="selectedMakeModel"
                        :inventoryType="selectedTypeStatus"
                        :forceTransmissionSelection="false"
                        :allowRestore="true"
                        v-on:restore="onRestoreMakeModel"
                        :inventoryNotes="currentInventory.notes"
                        :vinNo="currentInventory.vinNo"
                        :inventoryId="currentInventory.id"
                        :purchasedFromType="currentInventory.purchasedFromType"
                        :carfaxRating="carfaxRatingForMatch"
                        :vinDecodeName="vinDecodeNameForDisplay"
                        :vinDecodeDetails="vinDecodeDetailsForDisplay"
                        v-on:carfax-match-requested="requestCarfaxMakeModelCheck"
                        :showCarfaxComparison="showCarfaxModelNameInformation || showCarfaxFieldDifferences"
                        v-on:configuring-change="makeModelSelectorOpen = $event"
                        v-on:carfax-apply="showCarfaxDecodedVehicleDetails"
                        v-on:carfax-dismiss="closeMakeModelNameInformationModal"
                        ref="makeModelSelectorRef">
                      </make-model-selector>
                      <span v-show="errors.has('selectedMakeModel')" class="help is-danger"><i v-show="errors.has('selectedMakeModel')" class="fas fa-exclamation-triangle"></i> {{ errors.first('selectedMakeModel') }}</span>
                    </div>
                    <div class="column is-3 is-narrow">
                      <button-list-selector
                        name="PpaIntention"
                        validateAs="ppa intention"
                        label="PPA Intention"
                        v-model="selectedPpaIntention"
                        :allowDeselection="true"
                        v-validate="'required'"
                        :required="true"
                        :availableValues="ppaIntentionTypes">
                      </button-list-selector>
                      <span v-show="errors.has('PpaIntention')" class="help is-danger"><i v-show="errors.has('PpaIntention')" class="fas fa-exclamation-triangle"></i> The PPA Intention field is required</span>
                    </div>
                    <div class="column is-narrow is-4">
                      <button-list-selector :allowDeselection="true" validateAs="cpo intention" label="CPO Intention" v-model="selectedCpoStatus" :availableValues="definedTypes.cpoStatusTypes" v-if="showCpoStatus && this.currentDealer && !this.currentDealer.isWholesaleDealer"></button-list-selector>
                    </div>
                    <div class="column is-narrow is-5">
                      <button-list-selector validateAs="condition type" label="Condition" :required="false" v-model="selectedConditionType" :availableValues="definedTypes.inventoryConditionType"></button-list-selector>
                    </div>

                    <div class="column is-6 is-narrow">
                      <button-list-selector name="DispositionIntention" validateAs="disposition intention" label="Disposition Intention" v-validate="'required'" :required="true" v-model="selectedDispositionIntentionStatus" :availableValues="definedTypes.dispositionIntentionStatuses"></button-list-selector>
                      <span v-show="errors.has('DispositionIntention')" class="help is-danger"><i v-show="errors.has('DispositionIntention')" class="fas fa-exclamation-triangle"></i> A disposition intention selection is required</span>
                    </div>
                    <div class="column is-6 is-narrow">
                      <div class="field" v-if="selectedDispositionIntentionStatus && selectedDispositionIntentionStatus.id === 3">
                        <label class="label">Retail Day Limit<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect @select="onFormChange()" :taggable="true" @tag="wholesaleDayAdded" :showLabels="false" name="dispositionValidDays" data-vv-validate-on="input|close" data-vv-as="wholesale after days" v-validate="'required'" :class="{'is-danger': errors.has('dispositionValidDays') }" v-model="currentInventory.dispositionValidDays" :options="wholesaleLimitPeriods" :hideSelected="true"></multiselect>
                          <span v-show="errors.has('dispositionValidDays')" class="help is-danger"><i v-show="errors.has('dispositionValidDays')" class="fas fa-exclamation-triangle"></i> {{ errors.first('dispositionValidDays') }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="column is-3 is-narrow">
                      <div class="field">
                        <label class="label">Mileage<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control is-vertical-center">
                          <input name="usage" data-vv-as="miles" class="mileage-input" v-validate="{'numeric': true, 'required': true}" :class="{'input': true, 'is-width-100': true, 'is-danger': errors.has('usage') }" type="number" placeholder="#####" v-model="currentInventory.usage" :min="mileageEstimate ? 0 : minimumUsageAllowed">
                        </div>
                        <span v-show="errors.has('usage')" class="help is-danger"><i v-show="errors.has('usage')" class="fas fa-exclamation-triangle"></i> {{ errors.first('usage') }}</span>
                      </div>
                    </div>
                    <div class="column is-3 is-narrow">
                        <label class="label">Mileage Estimated<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <b-checkbox name="mileageEstimate" class="mileage-checkbox mt-5" size="is-medium" type="is-info" v-model="mileageEstimate"></b-checkbox>
                    </div>
                    <div class="column is-3 is-narrow">
                      <label  v-if="mileageEstimate" class="label">True Mileage Unknown<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                      <b-checkbox v-if="mileageEstimate" name="trueMileageUnknown" class="mileage-checkbox mt-5" size="is-medium" type="is-info" v-model="trueMileageUnknown"></b-checkbox>
                    </div>
                    <div class="column is-3 is-narrow" v-if="mileageEstimate">
                      <label class="label">Mileage Exempt</label>
                      <b-checkbox name="mileageExempt" class="mileage-checkbox mt-5" size="is-medium" type="is-info" v-model="mileageExempt"></b-checkbox>
                    </div>

                    <div class="column is-narrow is-6" v-if="selectedDispositionIntentionStatus && selectedDispositionIntentionStatus.id === 2">
                      <switch-selector type="is-info" label="Has Commitment" :required="false" v-model="hasCommitment" extraStyles="margin-top:12px;"></switch-selector>
                    </div>
                    <div class="column is-narrow is-6" v-if="selectedDispositionIntentionStatus && selectedDispositionIntentionStatus.id === 2 && hasCommitment">
                      <supplier-selector validateAs="commitment from" label="Commitment From" v-model="selectedCommitmentFrom" :required="true"></supplier-selector>
                    </div>

                    <div class="column is-narrow is-6" v-if="selectedDispositionIntentionStatus && selectedDispositionIntentionStatus.id === 2 && hasCommitment">
                      <switch-selector type="is-info" label="Firm Commitment" :required="false" v-model="currentInventory.firmCommitment" extraStyles="margin-top:12px;"></switch-selector>
                    </div>
                    <div class="column is-narrow is-6" v-if="selectedDispositionIntentionStatus && selectedDispositionIntentionStatus.id === 2 && hasCommitment">
                      <div class="field">
                        <label class="label no-content-wrap">Commitment Value<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-left">
                          <money name="commitmentValue" data-vv-as="commitment value" v-validate="'decimal|required'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('commitmentValue') }" type="text" placeholder="0.00" v-model="currentInventory.commitmentValue" v-bind="$globalMoneyFormat"></money>
                          <span v-show="errors.has('commitmentValue')" class="help is-danger"><i v-show="errors.has('commitmentValue')" class="fas fa-exclamation-triangle"></i> {{ errors.first('commitmentValue') }}</span>
                          <span class="icon is-small is-left">
                            <i class="fas fa-dollar-sign"></i>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div class="column is-6 is-narrow">
                      <label class="label">{{ proposedCostLabel }}<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                      <div class="field is-horizontal">
                        <div class="control has-icons-left">
                          <money name="proposedCost" data-vv-as="proposed cost" v-validate="'decimal|required'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('proposedCost'), 'is-width-200': true }" type="text" placeholder="0.00" v-on:blur="onProposedCostChange" v-model="currentInventory.proposedCost" v-bind="$globalMoneyFormat"></money>
                          <span v-show="errors.has('proposedCost')" class="help is-danger"><i v-show="errors.has('proposedCost')" class="fas fa-exclamation-triangle"></i> {{ errors.first('proposedCost') }}</span>
                          <span class="icon is-small is-left">
                            <i class="fas fa-dollar-sign"></i>
                          </span>
                        </div>
                        <i v-show="showCopyAcv" class="fal fa-arrow-alt-from-top fa-lg clickable has-text-success has-text-weight-bold" title="Click to copy ACV value to proposed trade allowance." style="margin-top: 10px; margin-left: 15px;" @click.stop.prevent="onCopyProposedCostToTradeAllowance()"/>
                      </div>
                      <label v-if="initialProposedCost > 0" class="label-frontgp is-pulled-left mr-5" :class="{'has-text-danger':(initialProposedCost && currentInventory && initialProposedCost !== currentInventory.proposedCost), 'has-text-success': initialProposedCost && currentInventory && initialProposedCost === currentInventory.proposedCost}" @click="setInitialProposedCostAsCurrent" title="Click to set the current proposed cost to the initial value.">{{initialProposedCost | currency}}</label>
                      <label v-if="onLoadProposedCost !== currentInventory.proposedCost" class="label-frontgp is-pulled-left" :class="{'has-text-danger':(onLoadProposedCost && currentInventory && onLoadProposedCost !== currentInventory.proposedCost), 'has-text-success': onLoadProposedCost && currentInventory && onLoadProposedCost === currentInventory.proposedCost}" @click="resetProposedCostAsCurrent" title="Click to reset proposed cost.">{{onLoadProposedCost | currency}}</label>
                    </div>
                    <div class="column is-6 is-narrow">
                      <b-field :label="proposedCostExpirationLabel" class="is-marginless">
                        <el-date-picker
                          class="is-width-200"
                          @change="onFormChange()"
                          v-model="currentInventory.proposedCostExpirationDate"
                          type="datetime"
                          placeholder="Click to select..."
                          popper-class="datepicker-container"
                          :picker-options="expirationDatePickerOptions">
                        </el-date-picker>
                      </b-field>
                      <label class="label-frontgp is-pulled-left" :class="{'has-text-success': true}" @click="onSetProposedExpirationDate" title="Click to use this date as a proposed expiration date.">{{defaultProposedExpirationDate}}</label>
                    </div>
                    <div class="column is-6 is-narrow"  v-if="isTradePpaIntention">
                      <label class="label">Proposed Trade Allowance</label>
                      <div class="control has-icons-left">
                        <money name="proposedTradeAllowance" data-vv-as="proposed trade allowance" v-validate="{'required': proposedTradeAllowanceRequired, 'decimal': true}" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('proposedTradeAllowance'), 'is-width-200': true }" type="text" placeholder="0.00" v-model="proposedTradeAllowance" v-bind="$globalMoneyFormat" :disabled="!hasTradeAllowanceModifyAccess"></money>
                        <span v-show="errors.has('proposedTradeAllowance')" class="help is-danger"><i v-show="errors.has('proposedTradeAllowance')" class="fas fa-exclamation-triangle"></i> {{ errors.first('proposedTradeAllowance') }}</span>
                        <span class="icon is-small is-left">
                          <i class="fas fa-dollar-sign"></i>
                        </span>
                      </div>
                    </div>
                    <div class="column is-6 is-narrow">
                      <label class="label">Payoff Amount</label>
                      <div class="control has-icons-left">
                        <money name="payoffAmount" data-vv-as="payoff amount" v-validate="'decimal'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('payoffAmount'), 'is-width-200': true }" type="text" placeholder="0.00" v-model="currentInventory.payoffAmount" v-bind="$globalMoneyFormat"></money>
                        <span v-show="errors.has('payoffAmount')" class="help is-danger"><i v-show="errors.has('payoffAmount')" class="fas fa-exclamation-triangle"></i> {{ errors.first('payoffAmount') }}</span>
                        <span class="icon is-small is-left">
                          <i class="fas fa-dollar-sign"></i>
                        </span>
                      </div>
                    </div>

                    <div class="column is-narrow is-4">
                      <div class="field">
                        <switch-selector type="is-info" label="Has vehicle been visually inspected?" v-model="currentInventory.ppaExamined"></switch-selector>
                        <label class="label-frontgp is-pulled-left" v-tooltip="'Examined By/Examined At'" v-if="currentInventory.ppaExamined && currentInventory.PpaExaminedBy !== ''">{{ppaInfo}}</label>
                      </div>
                    </div>
                    <div class="column is-narrow is-4" style="display: flex !important;">
                      <switch-selector type="is-info" label="Create service inspection?" v-model="currentInventory.serviceInspectionRequest" :isDisabled="currentInventory.hasPpaPreInspection"></switch-selector>
                      <i class="grid-icon fa-fw fal fa-wrench fa-lg" style="padding-top: 30px !important;" :class="getColorClassByGetReadyStatus(currentInventory.getReadyStatusTypeId)" v-tooltip="getReadyStatus" v-if="currentInventory.hasPpaPreInspection"/>
                    </div>
                    <div class="column is-narrow is-4" style="display: flex !important;">
                      <switch-selector type="is-info" label="Value is dependent on vehicle inspection?" v-model="currentInventory.inspectionDependent"></switch-selector>
                    </div>

                    <div class="column is-narrow is-12" v-if="proposedCostChanged">
                      <div class="field">
                        <label class="label">Reason for price re-evaluation<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control is-fullwidth">
                          <multiselect
                            :multiple="false"
                            v-validate="{'required': true}"
                            data-vv-as="reason for price re-evaluation"
                            data-vv-validate-on="input|close"
                            :class="{'is-danger': errors.has('priceAmendmentReason') }"
                            name="priceAmendmentReason"
                            label="name"
                            track-by="altName"
                            v-model="selectedPriceAmendmentReason"
                            :options="this.definedTypes.ppaRevisionReasonType.options"
                            :disabled="false">
                          </multiselect>
                          <span v-show="errors.has('priceAmendmentReason')" class="help is-danger"><i v-show="errors.has('priceAmendmentReason')" class="fas fa-exclamation-triangle"></i> {{ errors.first('priceAmendmentReason') }}</span>
                        </div>
                      </div>
                      <div class="field" v-if="proposedCostChanged">
                        <label class="label">Note</label>
                        <div class="control">
                          <textarea name="note" data-vv-as="note" v-validate="{'required': false}" :class="{'textarea': true, 'is-danger': errors.has('note') }" placeholder="" v-model="currentInventory.ppaPriceAmmendmentNote"></textarea>
                        </div>
                        <span v-show="errors.has('note')" class="help is-danger"><i v-show="errors.has('note')" class="fas fa-exclamation-triangle"></i> {{ errors.first('note') }}</span>
                      </div>
                    </div>

                    <div class="column is-12">
                      <div class="box">
                        <h6 class="title is-size-6">Proposed Costs</h6>
                        <b-table
                          :sortable="false"
                          :mobile-cards="false"
                          :selecteable="false"
                          :data="currentInventory.proposedCosts">
                          <template slot-scope="props">
                            <b-table-column field="proposedCost" label="Proposed Cost">
                              {{ props.row.proposedCost | currency }}
                            </b-table-column>
                            <b-table-column field="proposedTradeAllowance" label="Proposed Trade Allowance">
                              {{ props.row.proposedTradeAllowance | currency }}
                            </b-table-column>
                            <b-table-column field="ppaIntention" label="PPA Intention">
                              {{ props.row.ppaIntention }}
                            </b-table-column>
                            <b-table-column field="createdAt" label="Created At">
                              {{ props.row.createdAt | formatDateOnly }}
                            </b-table-column>
                            <b-table-column field="createdByName" label="Created By">
                              {{ props.row.createdByName }}
                            </b-table-column>
                          </template>

                          <template slot="empty">
                            <section class="section">
                              <div class="has-text-centered">
                                <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                                <p>No proposed costs.</p>
                              </div>
                            </section>
                          </template>
                        </b-table>
                      </div>
                    </div>
                    <div class="column">
                      <div v-if="approvePpaAppraisalCheckComplete && approvePpaCarFaxCheckComplete && approvePpaAutoCheckCheckComplete && blackBookCheckComplete && 
                        (!approvePpaCarFaxIsGood || !approvePpaAppraisalIsGood || !approvePpaAutoCheckIsGood || !blackBookCheckIsGood || purchaseCostExceededDealerTolerance)" class="verification-container column is-12">
                        <h4 class="title is-size-7 has-text-danger" v-if="approvePpaCarFaxFailed">Unable to get latest CarFax report. Please contact your administrator for investigation</h4>
                        <h4 class="title is-size-7 has-text-danger" v-else-if="!approvePpaCarFaxIsGood">The latest CarFax report indicated multiple owners, a recall pending or some form of damage.</h4>
                        <!-- <h4 class="title is-size-7 has-text-danger" v-if="autoCheckEnabled && approvePpaAutoCheckFailed">Unable to get latest AutoCheck report. Please contact your administrator for investigation</h4>
                        <h4 class="title is-size-7 has-text-danger" v-else-if="autoCheckEnabled && !approvePpaAutoCheckIsGood">The latest AutoCheck report indicated multiple owners, a recall pending or some form of damage.</h4> -->
                        <h4 class="title is-size-7 has-text-danger" v-if="approvePpaAppraisalFailed">Unable to get latest appraisal report. Please contact your administrator for investigation</h4>
                        <h4 class="title is-size-7 has-text-danger" v-else-if="!approvePpaAppraisalIsGood || purchaseCostExceededDealerTolerance">The proposed purchase price OR asking price of this vehicle is outside your dealer's acceptable market value tolerance.</h4>
                        <h4 class="title is-size-7 has-text-danger" v-if="blackBookEnabled && blackBookCheckFailed">Unable to get latest black book report. Please contact your administrator for investigation</h4>
                        <h4 class="title is-size-7 has-text-danger" v-else-if="blackBookEnabled && !blackBookCheckIsGood">Unable to obtain a complete black book report for this vehicle.</h4>
                        <h4 class="title is-size-7">Please Note: By activating the switch below you acknowledge the above vehicle's current status and accept responsibility for the purchase and/or sale of the vehicle and I am fully aware of the conditions and market value of the vehicle.</h4>
                        <div class="columns">
                          <b-switch class="column is-narrow is-vcentered" type="is-info" v-model="approvePpaAcceptConditions"></b-switch>
                          <h4 class="title is-size-7 column is-narrow is-vcentered" v-if="approvePpaAcceptConditions">I ACCEPT the above statement</h4>
                          <h4 class="title is-size-7 column is-narrow is-vcentered" v-if="!approvePpaAcceptConditions">I DO NOT ACCEPT the above statement</h4>
                        </div>
                      </div>
                      <div class="status-container column is-12" v-if="approvePpaAppraisalCheckComplete && approvePpaCarFaxCheckComplete && carFaxApproved && appraisalApproved && autoCheckValidated && blackBookCheckComplete && blackBookCheckIsGood">
                          <h4 class="title is-size-7 has-text-primary">{{approvalInformation}}</h4>
                        </div>
                      <div class="status-container column is-12" v-else-if="approvePpaAppraisalCheckComplete && approvePpaCarFaxCheckComplete && approvePpaCarFaxIsGood && approvePpaAppraisalIsGood && autoCheckValidated && blackBookCheckComplete && blackBookCheckIsGood">
                        <h4 class="title is-size-7 has-text-primary">The vehicle condition matches your dealers requirements and is approved for purchase.</h4>
                      </div>
                      <div class="status-container column is-12" v-if="!approvePpaAppraisalCheckComplete || !approvePpaCarFaxCheckComplete || !approvePpaAutoCheckCheckComplete || !blackBookCheckComplete">
                        Checking latest vehicle reports. Please wait...
                      </div>
                    </div>
                  </div>
                </div>

                <div class="column is-6">
                  <div class="columns is-multiline">
                    <div class="column is-12">
                      <div>
                        <div class="columns box is-multiline is-centered vauto-status-container" style="position: relative; z-index: 1999; width:98%; margin-left:10px;">
                          <div class="vauto-container-display-only column is-12">
                            <div style="width:100%">
                              <h3 data-v-74fd810e="" class="title is-6 is-inline-block is-marginless">Vehicle Reports</h3>
                            </div>
                          </div>
                          <div class="column is-12">
                            <div class="column is-12">
                              <car-fax-control
                                :inventory="currentInventory"
                                title="CarFax Vehicle Status"
                                :carFaxRating="currentCarfaxRating"
                                :vinNo="currentInventory.vinNo"
                                :displayOnly="false"
                                v-on:on-new-carfax="onApprovePpaNewCarfax"
                                v-on:on-carfax-failed="onCarFaxForApprovePpa"
                                :runAutomatically="true"
                                :autoUpdateInventory="true"
                                :modalContainer="true"
                                v-on:on-show-carfax-report="onShowCarFaxReport"
                                :isBoxed="false"
                                v-on:on-carfax-vehicle-information="onCarFaxVehicleInformation"
                                v-on:on-carfax-requested="onCarFaxRequested"
                                :usePdfModalPortal="false"
                                v-on:on-carfax-payment-required="onCarfaxPaymentRequired"
                                v-on:on-carfax-purchase-requested="onCarfaxPaymentRequested"
                                :resolveActionDisabled="true">
                              </car-fax-control>
                            </div>
                            <carfax-overview-component
                            :inventoryItem="currentInventory"
                            :carFaxRating="currentCarfaxRating"
                            ></carfax-overview-component>
                            <hr class="block-hr-divider" />
                          </div>
                          <div class="column is-12">
                            <div>
                              <div class="columns is-multiline is-centered vauto-status-container" style="position: relative; z-index: 1999;">
                                <div class="vauto-container-display-only column is-12">
                                  <div style="width:100%">
                                    <h3 data-v-74fd810e="" class="title is-6 is-inline-block is-marginless">vAuto Vehicle Status</h3>
                                  </div>
                                </div>
                                <div class="column is-12">
                                  <a v-if="hasVautoAppraisal" :href="currentInventory.vAutoAppId" target="_vauto" title="Click to view the latest vAuto report for this vehicle."><img src="@/assets/static/vauto/vauto_logo_small.png" style="width:90px; height:auto;"></a>
                                  <img v-else title="vAuto report is pending" src="@/assets/static/vauto/vauto_logo_small.png" style="width:90px; height:auto;" class="img-disabled">
                                </div>
                              </div>
                            </div>
                            <hr class="block-hr-divider" />
                          </div>
                          <div class="column is-12"></div>
                          <!-- <div class="column is-12">
                            <auto-check-control
                              v-if="autoCheckEnabled"
                              :inventory="currentInventory"
                              title="AutoCheck Vehicle Status"
                              :autoCheckRating="currentInventory.autoCheckRating"
                              :vinNo="currentInventory.vinNo"
                              :displayOnly="false"
                              v-on:on-new-autocheck="onAutoCheckForApprovePpa"
                              v-on:on-autocheck-failed="onAutoCheckForApprovePpa"
                              :runAutomatically="true"
                              :autoUpdateInventory="true"
                              :modalContainer="true"
                              v-on:on-show-autocheck-report="onShowAutoCheckReport"
                              :showRefresh="false"
                              :purchasedFromType="currentInventory.purchasedFromType"
                              :inventoryType="selectedInventoryStatusType"
                              v-on:autocheck-disabled="onAutoCheckDisabled">
                            </auto-check-control>
                            <hr class="block-hr-divider" />
                          </div>
                          <div class="column is-12"></div> -->
                          <div class="column is-12" style="padding-left:50px;padding-right:50px;">
                            <section-header title="Damage History" :collapsed="isDamageContainerCollapsed"></section-header>
                            <div>
                              <b-table
                                :sortable="false"
                                :mobile-cards="false"
                                :selecteable="false"
                                :data="damageHistory">
                                <template slot-scope="props">
                                  <b-table-column field="value" label="Description">
                                    <span v-tooltip="props.row.value">{{ props.row.value }}</span>                                     
                                  </b-table-column>
                                  <b-table-column field="createdAt" label="Created At" style="padding-left:5px !important; min-width: 100px !important">
                                    {{ props.row.createdAt | formatDateOnly }}
                                  </b-table-column>
                                  <b-table-column field="createdByName" label="Created By" style="padding-left:5px !important; min-width: 100px !important">
                                    {{ props.row.createdByName }}
                                  </b-table-column>
                                </template>

                                <template slot="empty">
                                  <section class="section">
                                    <div class="has-text-centered">
                                      <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                                      <p>No damage history.</p>
                                    </div>
                                  </section>
                                </template>
                              </b-table>
                            </div>
                            <hr class="block-hr-divider" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="column is-12 mt-10" v-if="inventoryItem.latestVAutoReport">
                      <inventory-vauto-control :key="'va' + currentInventory.id" :vAutoRating="inventoryItem.latestVAutoReport" :inventory="currentInventory" displayMode="Compact"></inventory-vauto-control>
                    </div>
                    <div class="column is-12 mt-10">
                      <inventory-appraisal-control
                        :key="currentInventory.id"
                        v-if="hasFeatureAccess('inventory.appraisal.view')"
                        :appraisedValue="mostRecentAppraisal"
                        displayMode="Compact"
                        :inventory="inventoryAppraisalParams"
                        v-on:on-new-appraisal="onAppraised"
                        v-on:on-appraisal-failed="onAppraised"
                        :runAutomatically="true"
                        :autoUpdateInventory="true"
                        :inventoryType="currentInventory.type">
                      </inventory-appraisal-control>
                    </div>
                    <div class="column is-12 mt-10" v-if="blackBookEnabled">
                      <inventory-black-book-control
                        :key="'ibbc' + instanceId"
                        :blackBookInformation="mostRecentBlackBookInformation"
                        :inventory="inventoryAppraisalParams"
                        v-on:on-new-blackbook-information="onBlackBookInformationRetrieved"
                        :runAutomatically="true"
                        :autoUpdateInventory="true"
                        :inventoryType="currentInventory.type"
                        v-on:on-blackbook-information-failed="onBlackBookInformationRetrieved"
                        v-on:black-book-disabled="onBlackBookDisabled">
                      </inventory-black-book-control>
                    </div>
                    <div class="column is-12 mt-10">
                      <customer-info-control
                        :key="currentInventory.id"
                        :runAutomatically="true"
                        :customerNameLabel="'Purchased From'"
                        :customerId="currentInventory.purchasedFromId"
                        :customerMobile="currentInventory.purchasedFromContactMobile"
                        :customerEmail="currentInventory.purchasedFromContactEmail"
                        :inventoryId="currentInventory.id"
                        v-on:lead-status="setLeadStatusValue">
                      </customer-info-control>
                    </div>
                    <div class="column is-12 mt-10">
                      <div class="box">
                        <section-header title="Attachments"></section-header>
                        <div class="columns">
                          <div class="column is-4">
                            <button-list-selector validateAs="window sticker status" label="Window Sticker Status" :required="windowStickerStatusRequired" v-model="selectedWindowStickerStatus" :availableValues="definedTypes.windowStickerStatusOptions"></button-list-selector>
                          </div>
                          <div class="column is-4">
                            <image-selector
                              v-model="windowStickerFiles"
                              label="Window Sticker Image"
                              :disabled="windowStickerDisabled"
                              :required="false"
                              :isBoxed="false"
                              v-if="selectedWindowStickerStatus && selectedWindowStickerStatus.id === 0"
                              @uploadStarted="uploadStarted"
                              @uploadComplete="uploadComplete">
                            </image-selector>
                          </div>
                          <div class="column is-4">
                            <pdf-selector
                            v-model="windowStickerPdfFiles"
                            label="Window Sticker Pdf"
                            :enabled="!windowStickerDisabled"
                            :required="false"
                            :isBoxed="false"
                            v-if="selectedWindowStickerStatus && selectedWindowStickerStatus.id === 0"
                            @uploadStarted="uploadStarted"
                            @uploadComplete="uploadComplete"
                            :usePdfModalPortal="false"></pdf-selector>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </section>
          <footer class="modal-card-foot">
            <div class="columns" style="width: 100%">
              <div class="column is-4 is-pulled-left">
              <button class="button is-success" v-if="!approvedSuccessfully && !isPpaClosed" :class="{'is-loading': isSaving }" @click="validateBeforeSubmit" :disabled="approvePpaConfirmDisallowed || isUploadingFile">Confirm</button>
                <button class="button is-danger" @click="onCancel()">{{approvedSuccessfully ? "Close" : "Cancel"}}</button>
              </div>
              <div class="column is-4" v-if="isIndividual">
                <div class="field is-grouped" style="justify-content: center">
                  <div class="control">
                    <div class="tags has-addons">
                      <span class="tag is-dark">Customer Name</span>
                      <span class="tag is-success">
                        {{ currentInventory.purchasedFromName }}
                        <customer-pop-component class="ml-2" :isDarkMode="true" v-model="currentInventory.purchasedFromId"></customer-pop-component>
                      </span>
                    </div>
                  </div>
                  <div class="control" v-if="leadStatusValue">
                    <div class="tags has-addons">
                      <span class="tag is-dark">Lead Status</span>
                      <span class="tag is-success has-text-weight-semibold">{{ leadStatusValue }}</span>
                    </div>
                  </div>
                  <div class="control" v-if="leadTradeValue">
                    <div class="tags has-addons">
                      <span class="tag is-dark">Trade</span>
                      <span class="tag is-success">{{ leadTradeValue }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="column is-4" v-else>
              </div>
              <div class="column is-4">
                <div class="is-pulled-right is-flex">
                  <inventory-recall-resolve-action :currentInventory="currentInventory" :usePortal="false" :isQuickView="false"></inventory-recall-resolve-action>
                  <convert-ppa-to-purchase-action v-if="false"
                    :key="'convert-ppa' + currentInventory.id"
                    :inventory="currentInventory"
                    :usePortal="false"
                    :invertSecondUsePortal="true">
                  </convert-ppa-to-purchase-action>
                  <close-ppa-action v-if="!isPpaClosed" :inventoryItem="currentInventory" :usePortal="false" v-on:on-ppa-cancelled="onPpaCancelled" :leadStatus="leadStatusValue" :syncLeadStatus="false" ref="closePpaAction"></close-ppa-action>
                  <div class="tags are-medium" v-if="purchasedFromNameFallback !== ''">
                    <click-to-contact v-if="buildBuyerInfo" :key="'ctc-' + purchasedFromIdFallback" :value="buildBuyerInfo" :isPpaApprovalAction="true"></click-to-contact>
                  </div>
                </div>
              </div>
              <!-- <div class="column is-4">
                <title-status-capture v-if="currentInventory" :currentInventory="currentInventory" :showDialog="false" :isListMode="false" :forPpaApproval="true"></title-status-capture>
              </div> -->
            </div>
          </footer>
        </div>
      </b-modal>
    </portal>
    <portal to="global-modal-portal" v-if="confirmTradeAllowanceChangeActive" :order="2">
      <b-modal :active.sync="confirmTradeAllowanceChangeActive" :width="640" scroll="keep" :has-modal-card="true">
        <!-- ⚠ An explicit width, matching the :width="640" above. `width: auto` collapsed this card
             to ~219px: these two modals are PORTALED into global-modal-portal, whose flex container
             shrink-to-fits an auto-width child, so the info sentence was squeezed out entirely and
             the comparison table was unreadable. EditInventory's copy is not portaled, which is why
             the same markup looks right there. max-width keeps it responsive on a phone. -->
        <div class="modal-card" style="width: 640px; max-width: 100%; max-height: 480px !important;">
          <header class="modal-card-head">
              <p class="modal-card-title">Change Proposed Trade Allowance</p>
          </header>
          <section class="modal-card-body">
              Do you want to change the proposed trade allowance of this ppa to match the ACV value?
          </section>
          <footer class="modal-card-foot">
            <button class="button is-success" @click="confirmTradeAllowanceChange()" type="button">Yes</button>
            <button class="button is-danger" @click="closeTradeAllowanceModal()" type="button">No</button>
          </footer>
        </div>
      </b-modal>
    </portal>
    <auto-check-report-control
      :inventory="currentInventory"
      title="AutoCheck Vehicle Status"
      :vinNo="currentInventory.vinNo"
      v-on:on-hide-autocheck-report="onHideAutoCheckReport"
      :showAutoCheckReportModal="showReport"
      v-if="currentInventory && autoCheckEnabled">
    </auto-check-report-control>
    <car-fax-report-control
      title="CarFax Vehicle Status"
      v-on:on-hide-carfax-report="onHideCarFaxReport"
      :showCarFaxReportModal="showCarFaxReport"
      :carFaxReportUrl="carFaxReportUrl"
      v-if="currentInventory">
    </car-fax-report-control>
    
    <!-- ⚠ Not rendered while the make/model selector modal is open - that modal shows the same
         comparison inline instead. Two presentations of one flag: this modal is the one whose "Yes"
         OPENS the selector, so it only makes sense when the selector is closed. Before this gate both
         rendered at once, at the same z-index, and document order alone decided which covered which. -->
    <portal to="global-modal-portal" v-if="showCarfaxModelNameInformation && !makeModelSelectorOpen" :order="2">
      <b-modal :active="showCarfaxModelNameInformation" :width="640" scroll="keep" :has-modal-card="true">
        <!-- ⚠ An explicit width, matching the :width="640" above. `width: auto` collapsed this card
             to ~219px: these two modals are PORTALED into global-modal-portal, whose flex container
             shrink-to-fits an auto-width child, so the info sentence was squeezed out entirely and
             the comparison table was unreadable. EditInventory's copy is not portaled, which is why
             the same markup looks right there. max-width keeps it responsive on a phone. -->
        <div class="modal-card" style="width: 640px; max-width: 100%; max-height: 480px !important;">
          <header class="modal-card-head">
            <p class="modal-card-title">CARFAX</p>
          </header>
          <section class="modal-card-body">
            <!-- ⚠ The model CHOICE table, not the five-field diff. The old two-column Yes/No offered
                 exactly two answers - take CARFAX's value or take nothing - and CARFAX returns the model and
                 series as SEPARATE fields ("812" + "Superfast") while the catalogue row is named
                 "812 Superfast". So Yes wrote "812" as free text: no catalogue link, dropped from model
                 reporting. This offers the catalogue rows behind the group instead. -->
            <carfax-model-reconcile
              ref="carfaxModelReconcile"
              :selectedMakeModel="selectedMakeModel"
              :carfaxMakeModel="carfaxMakeModel"
              :selectedMake="carfaxMakeModel && carfaxMakeModel.selectedMake"
              v-on:choice-changed="carfaxReconcileApplyable = $event"
              v-on:apply="onCarfaxModelReconcileApply">
            </carfax-model-reconcile>
          </section>
          <!-- ⚠ Apply is DISABLED until a row is ticked. The table pre-checks the current model, so Apply
               with no interaction changes no model - but a record with no model at all gets no current row,
               and an enabled Apply would then apply null. -->
          <footer class="modal-card-foot">
            <button class="button is-primary" type="button" data-test="carfax-reconcile-apply" :disabled="!carfaxReconcileApplyable" @click="$refs.carfaxModelReconcile.onApply()">Apply</button>
            <button class="button is-danger" type="button" data-test="carfax-reconcile-cancel" @click="closeMakeModelNameInformationModal">Cancel</button>
          </footer>
        </div>
      </b-modal>
    </portal>
    <ppa-inactive-lead-check-control :inventoryItem="currentInventory" :inventoryLeadsCheckRequested="customerSyncRequested" v-on:show-close-ppa-control="showClosePpaControl" :hasClosePpaAccess="hasClosePpaAccess" :validPpaStatus="showPpaCloseButton" :usePortal="true" :portalOrder="2"></ppa-inactive-lead-check-control>
  </span>
</template>

<script>

import eventBus from '@/eventBus'
import moment from 'moment'
import inventoryService from '../../services/inventoryService'
import ErrorDisplayComponent from '@/components/generic/ErrorDisplayComponent'
import validateBeforeSubmitMixin from '@/mixins/generic/validateBeforeSubmit'
import SwitchSelector from '@/components/generic/SwitchSelector'
import CarfaxModelReconcile from '@/components/generic/CarfaxModelReconcile'
import formattingMixin from '@/mixins/inventory/formatting'
import AutoCheckControl from '@/components/generic/AutoCheckControl'
import AutoCheckReportControl from '@/components/generic/AutoCheckReportControl'
import CarFaxControl from '@/components/generic/CarFaxControl'
import CarFaxReportControl from '@/components/generic/CarFaxReportControl'
import CustomerInfoControl from '@/components/generic/CustomerInfoControl'
import InventoryAppraisalControl from '@/components/generic/InventoryAppraisalControl'
import ButtonListSelector from '@/components/generic/ButtonListSelector'
import SupplierSelector from '@/components/generic/SupplierSelector'
import InventoryTitleCapture from '@/components/generic/inventory/InventoryTitleCapture'
import utilitiesMixin from '@/mixins/generic/utilities'
import ClosePpaAction from '@/components/inventory/ClosePpaAction'
import SectionHeader from '@/components/generic/SectionHeader'
import DealerPeakControl from '@/components/generic/DealerPeakControl'
import ConvertPpaToPurchaseAction from '@/components/inventory/ConvertPpaToPurchaseAction'
import ImageSelector from '@/components/generic/ImageSelector'
import PdfSelector from '@/components/generic/PdfSelector'
import InventoryBlackBookControl from '@/components/generic/InventoryBlackBookControl'
import InventoryVautoControl from '@/components/generic/InventoryVautoControl'
import MakeModelSelector from '@/components/generic/MakeModelSelector'
import CarfaxOverviewComponent from '@/components/inventory/CarfaxOverviewComponent'
import CarfaxProcessStatusHeader from '@/components/generic/CarfaxProcessStatusHeader'
import ClickToContactInfoComponent from '@/components/generic/ClickToContactInfoComponent'
import CustomerPopComponent from '@/components/generic/CustomerPopComponent'
import InventoryRecallResolveAction from '@/components/inventory/InventoryRecallResolveAction'
import PpaInactiveLeadCheckControl from '@/components/generic/inventory/PpaInactiveLeadCheckControl'

import { createNamespacedHelpers } from 'vuex'
import { cloneDeep } from 'lodash'
import { buildCarfaxComparison, carfaxMatchOutcome, carfaxModelNameDiffers } from '@/utils/carfax_make_model_comparison'
import { hasCarfaxTesterRole } from '@/utils/carfax_roles'
import { buildReconcileMessage, readMakeModelFields, buildCarfaxOverwriteNote, buildCarfaxOverwriteNoteModel, buildCarfaxCustomMakeModelValues } from '@/utils/carfax_make_model_resolution'
import { setInventoryAttribute } from '@/utils/inventory_attributes'
const mapDealerState = createNamespacedHelpers('dealer').mapState
const mapConfigGetters = createNamespacedHelpers('config').mapGetters
const mapUserGetters = createNamespacedHelpers('user').mapGetters
const mapVehicleMakeGetters = createNamespacedHelpers('vehicleMake').mapGetters

export default {
  name: 'ApprovePpaAction',
  mixins: [validateBeforeSubmitMixin, formattingMixin, utilitiesMixin],
  props: {
    inventoryItem: {
      type: Object,
      default: null
    },
    showStatusIcon: {
      type: Boolean,
      default: false
    },
    usePortal: {
      type: Boolean,
      default: true
    },
    reloadList: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      //⚠ Mirrors the make/model selector modal's own isConfiguring, reported up through
      //configuring-change. It exists because $refs are not reactive, so the v-if on the CARFAX
      //prompt below cannot read the child's state directly.
      makeModelSelectorOpen: false,
      isApprovedPpaConfirmationActive: false,
      approvedSuccessfully: false,
      isSaving: false,
      serverErrors: [],
      serverErrorMessage: '',
      instanceId: this.$uuid.v4(),
      approvedPpaItem: null,
      selectedPriceAmendmentReason: null,
      onLoadProposedCost: 0,
      currentInventory: null,
      isLoading: false,
      initialProposedCost: 0,
      expirationDatePickerOptions: {
        disabledDate (date) {
          return date < new Date()
        }
      },
      approvePpaAutoCheckCheckComplete: true,
      approvePpaAutoCheckIsGood: true,
      approvePpaAutoCheckFailed: false,
      showReport: false,
      tabIndex: 0,
      modalWidth: (window.innerWidth * 70 / 100),
      approvePpaCarFaxCheckComplete: false,
      approvePpaCarFaxIsGood: false,
      approvePpaCarFaxFailed: false,
      showCarFaxReport: false,
      approvePpaAppraisalCheckComplete: false,
      approvePpaAppraisalIsGood: false,
      approvePpaAppraisalFailed: false,
      approvePpaAcceptConditions: false,
      selectedPpaIntention: null,
      selectedDispositionIntentionStatus: null,
      hasCommitment: false,
      selectedCommitmentFrom: null,
      selectedCpoStatus: null,
      selectedConditionType: null,
      confirmTradeAllowanceChangeActive: false,
      proposedTradeAllowance: 0,
      mileageEstimate: false,
      trueMileageUnknown: false,
      carFaxHashMatch: false,
      autoCheckHashMatch: false,
      appraisalRatingHashMatch: false,
      carFaxApproved: false,
      autoCheckApproved: false,
      appraisalApproved: false,
      windowStickerPdfFiles: [],
      windowStickerFiles: [],
      onLoadEstWholesaleValue: 0,
      windowStickerDisabled: false,
      selectedWindowStickerStatus: null,
      isUploadingFile: false,
      fileUploadCount: 0,
      blackBookInformation: null,
      blackBookCheckComplete: false,
      blackBookCheckIsGood: false,
      blackBookCheckFailed: false,
      callBackAppraisal: null,
      isDamageContainerCollapsed: true,
      damageHistory: [],
      selectedTypeStatus: null,
      selectedDealer: null,
      selectedMakeModel: null,
      currentCarfaxRating: null,
      carfaxMakeModel: null,
      //The note recording what the operator's Yes replaced, held until the approval payload is built.
      //Empty unless the carfax values were actually accepted - see showCarfaxDecodedVehicleDetails.
      carfaxOverwriteNote: '',
      showCarfaxModelNameInformation: false,
      //⚠ Separate from the flag above - see the inventory mixin. That one is the reconcile prompt and
      //offers Apply; this one is the read-only view shown when the model already agrees.
      /*
       * Whether the reconcile prompt currently has a row ticked, so the footer can disable Apply.
       *
       * ⚠ Declared up front. Vue 2 cannot make a key reactive after the fact, and every other flag
       * on this prompt had to be fixed for exactly that - a modal that never re-rendered.
       */
      carfaxReconcileApplyable: false,
      showCarfaxFieldDifferences: false,
      makeModelCheckRequestCompleted: false,
      carfaxRequested: false,
      leadStatusValue: '',
      leadTradeValue: '',
      carfaxModelNameInformation: '',
      hideCarfaxStatusHeader: false,
      customerSyncRequired: false,
      customerSyncRequested: false,
      closePpaRequestConfirmed: false
    }
  },
  components: {
    'carfax-model-reconcile': CarfaxModelReconcile,
    'error-display-component': ErrorDisplayComponent,
    'switch-selector': SwitchSelector,
    'auto-check-control': AutoCheckControl,
    'auto-check-report-control': AutoCheckReportControl,
    'car-fax-control': CarFaxControl,
    'car-fax-report-control': CarFaxReportControl,
    'customer-info-control': CustomerInfoControl,
    'inventory-appraisal-control': InventoryAppraisalControl,
    'button-list-selector': ButtonListSelector,
    'supplier-selector': SupplierSelector,
    'title-status-capture': InventoryTitleCapture,
    'close-ppa-action': ClosePpaAction,
    'section-header': SectionHeader,
    'dealer-peak-control': DealerPeakControl,
    'convert-ppa-to-purchase-action': ConvertPpaToPurchaseAction,
    'image-selector': ImageSelector,
    'pdf-selector': PdfSelector,
    'inventory-black-book-control': InventoryBlackBookControl,
    'inventory-vauto-control': InventoryVautoControl,
    'make-model-selector': MakeModelSelector,
    'carfax-overview-component': CarfaxOverviewComponent,
    'carfax-process-status-header': CarfaxProcessStatusHeader,
    'click-to-contact': ClickToContactInfoComponent,
    'customer-pop-component': CustomerPopComponent,
    'inventory-recall-resolve-action': InventoryRecallResolveAction,
    'ppa-inactive-lead-check-control': PpaInactiveLeadCheckControl
  },
  computed: {
    ...mapDealerState(['currentDealer']),
    ...mapConfigGetters(['definedTypes']),
    ...mapUserGetters(['hasFeatureAccess', 'currentUser', 'lastActiveListPage']),
    ...mapVehicleMakeGetters(['allVehicleMakes']),
    isMakeSoldByDealer: function () {
      if (!this.currentInventory) return false

      if (!this.currentInventory.vehicleMakeId) return false

      if (!this.currentDealer.sellsVehicleMakes.find((x) => x.vehicleMakeId === this.currentInventory.vehicleMakeId)) return false

      return true
    },
    showCpoStatus: function () {
      if (!this.inventoryItem) return false

      if (!this.isMakeSoldByDealer) return false

      if (this.currentDealer.isWholesaleDealer) return false

      if (!this.currentInventory.typeId) {
        return false
      } else {
        return [1, 3, 4, 5].indexOf(this.currentInventory.typeId) >= 0
      }
    },
    isTradePpaIntention: function () {
      return this.selectedPpaIntention && this.selectedPpaIntention.name === 'Trade'
    },
    defaultProposedExpirationDate: function () {
      let defaultDate = moment.tz(new Date(), moment.tz.guess()).add((this.currentDealer.prePurchaseInspectionExpiry || 0), 'day').format('YYYY-MM-DD 00:00:00')
      return defaultDate
    },
    ppaInfo: function () {
      return this.currentInventory && (this.currentInventory.ppaExaminedBy !== '' && this.currentInventory.ppaExaminedAt !== null) ? this.currentInventory.ppaExaminedBy + ' - ' + this.$options.filters.formatDateOnlyShort(this.currentInventory.ppaExaminedAt) : ''
    },
    hasApprovePpaAccess: function () {
      let allowedPermissions = ['inventory.approve-ppa']
      var userHasAccess = false
      allowedPermissions.forEach((x) => {
        userHasAccess = userHasAccess || this.hasFeatureAccess(x)
      })
      return userHasAccess
    },
    isTypePpaAppraisal: function () {
      return this.inventoryItem && this.inventoryItem.type === 'ForPpaAppraisal'
    },
    inventoryItemId: function () {
      return this.inventoryItem ? this.inventoryItem.id : null
    },
    modalTitle () {
      return this.approvedSuccessfully ? 'Pre-Purchase Appraisal Approved Successfully' : 'Confirm Value on Pre-Purchase Appraisal'
    },
    modalContent () {
      return this.approvedSuccessfully ? 'Inventory ' + this.stockNo + ' has been succesfully approved' : 'Are you sure you want to commit a value to the Pre-Purchase Appraisal - Inventory ' + this.stockNo
    },
    stockNo () {
      return this.inventoryItem ? this.inventoryItem.stockNo : ''
    },
    inventoryDetails () {
      return this.inventoryItem && this.inventoryItem.vehicleInfo ? this.inventoryItem.vehicleInfo : this.inventoryItem.name
    },
    canShowPpaAction () {
      return true
    },
    isApprovable () {
      return this.inventoryItem && this.isTypePpaAppraisal && !this.inventoryItem.ppaApproved
    },
    isIndividual: function () {
      return this.currentInventory && this.currentInventory.purchasedFromType === 'Individual'
    },
    applicableIconTitle () {
      if (!this.isTypePpaAppraisal) return 'Action is not applicable to this inventory item. (' + this.inventoryItem.stockNo + ')'
      if (this.inventoryItem && this.inventoryItem.ppaApproved) {
        if (this.isTradePpaIntention && !this.proposedTradeAllowance) {
          return 'PPA is approved for purchase but missing proposed trade allowance value. (' + this.inventoryItem.stockNo + ')'
        }
        return 'PPA is approved for purchase. (' + this.inventoryItem.stockNo + ')'
      }
      return 'Click to approve PPA (' + this.inventoryItem.stockNo + ')'
    },
    formatIconByStatus () {
      var result = 'has-text-grey-light'
      if (this.isTypePpaAppraisal) {
        if (this.inventoryItem && this.inventoryItem.ppaApproved && (this.inventoryItem.ppaApprovalAutoExpired === 1 || this.inventoryItem.ppaApprovalAutoExpired === true)) {
          result = 'has-text-warning'
        } else if (this.inventoryItem && this.inventoryItem.ppaApproved && this.isTradePpaIntention && !this.proposedTradeAllowance) {
          result = 'has-text-orange'
        } else if (this.inventoryItem && this.inventoryItem.ppaApproved) {
          result = 'has-text-success'
        } else {
          result = 'has-text-danger'
        }
      }
      let classToAttach = {}
      classToAttach[result] = true
      return classToAttach
    },
    getNotificationClass: function () {
      return this.approvedSuccessfully ? 'is-success' : 'is-info'
    },
    proposedCostChanged: function () {
      if (this.onLoadProposedCost && this.onLoadProposedCost > 0.01 && this.onLoadProposedCost !== this.currentInventory.proposedCost) {
        return true
      }
      return false
    },
    getReadyStatus: function () {
      return this.definedTypes.serviceRequestStatusType.options.find(x => x.id === this.currentInventory.getReadyStatusTypeId).name
    },
    selectedInventoryStatusType: function () {
      return this.definedTypes.inventoryTypes.find((x) => (x.name === this.currentInventory.type || x.altName === this.currentInventory.type))
    },
    carFaxReportUrl: function () {
      let result = 'https://www.carfaxonline.com/cfm/Display_Dealer_Report.cfm?partner=VAU_0&UID=' + this.currentDealer.carFaxUserName + '&vin=' + this.currentInventory.vinNo
      return result
    },
    hasVautoAppraisal: function () {
      return this.currentInventory.vAutoAppId && this.currentInventory.vAutoAppId !== ''
    },
    mostRecentAppraisal: function () {
      if (this.currentInventory && this.currentInventory.appraisals && this.currentInventory.appraisals.length > 0) {
        return this.currentInventory.appraisals[0]
      }
      return null
    },
    inventoryAppraisalParams: function () {
      let trimAttribute = this.currentInventory.attributes.filter((x) => x.name === 'Trim')[0]
      let trim = trimAttribute ? trimAttribute.value : ''
      let condition = this.currentInventory.conditionType
      return {
        id: this.currentInventory.id,
        vinNo: this.currentInventory.vinNo,
        mileage: this.currentInventory.usage,
        trim: trim,
        condition: condition,
        msrp: this.currentInventory.msrp,
        purchaseCost: this.resolvedPurchaseCost,
        askingPrice: this.currentInventory.askingPrice
      }
    },
    resolvedPurchaseCost: function () {
      if (this.currentInventory.type === 'Consigned') {
        return this.currentInventory ? this.currentInventory.consignmentMinimum : null
      }
      return this.purchaseCostTotal
    },
    purchaseCostTotal: function () {
      let purchaseCost = this.currentInventory && this.currentInventory.proposedCost ? this.currentInventory.proposedCost : null
      let commission = this.currentInventory && this.currentInventory.commission ? this.currentInventory.commission : null
      return purchaseCost + commission
    },
    approvePpaConfirmDisallowed: function () {
      if (this.approvePpaAcceptConditions === true) {
        return false
      }

      if (this.purchaseCostExceededDealerTolerance) {
        return true
      }

      if (!this.approvePpaCarFaxIsGood || !this.approvePpaAppraisalIsGood || !this.approvePpaAutoCheckIsGood) {
        return true
      }

      return false
    },
    ppaIntentionTypes: function () {
      return this.definedTypes.ppaIntentionType.options
    },
    proposedCostLabel: function () {
      return this.isTradePpaIntention ? 'ACV Value' : 'Proposed Purchase Price'
    },
    proposedCostExpirationLabel: function () {
      return this.isTradePpaIntention ? 'ACV Value Expiration Date' : 'Purchase Price Expiration Date'
    },
    proposedTradeAllowanceRequired: function () {
      return false
    },
    wholesaleLimitPeriods: function () {
      return this.currentDealer.wholesaleLimitPeriods
    },
    hasTradeAllowanceModifyAccess: function () {
      return this.hasFeatureAccess('inventory.modify.ppa-trade-allowance')
    },
    minimumUsageAllowed: function () {
      if (this.currentInventory && this.currentInventory.vinUsageHistory && this.currentInventory.vinUsageHistory.length > 0) {
        return this.currentInventory.vinUsageHistory[this.currentInventory.vinUsageHistory.length - 1].usage
      }
      return this.lastVinUsage || 0
    },
    showPpaConversionButton: function () {
      return this.currentInventory && this.currentInventory.ppaApproved && this.currentInventory.status !== 'Archived' && !this.isPpaClosed
    },
    ppaAgreementApprovals: function () {
      if (this.inventoryItem && this.inventoryItem.approvals) {
        let data = this.inventoryItem.approvals.filter((x) => x.approvalRatingType === 'PpaApproval')
        if (data.length > 0) {
          return data
        }
      }
      return []
    },
    hasAutoCheckRatingApproval: function () {
      if (this.ppaAgreementApprovals && this.ppaAgreementApprovals.length > 0) {
        let data = this.ppaAgreementApprovals.filter((x) => x.ratingType === 'AutoCheckRating')
        if (data.length > 0) {
          return true
        }
      }
      return false
    },
    hasCarFaxRatingApproval: function () {
      if (this.ppaAgreementApprovals && this.ppaAgreementApprovals.length > 0) {
        let data = this.ppaAgreementApprovals.filter((x) => x.ratingType === 'CarFaxRating')
        if (data.length > 0) {
          return true
        }
      }
      return false
    },
    hasAppraisalRatingApproval: function () {
      if (this.ppaAgreementApprovals && this.ppaAgreementApprovals.length > 0) {
        let data = this.ppaAgreementApprovals.filter((x) => x.ratingType === 'AppraisalRating')
        if (data.length > 0) {
          return true
        }
      }
      return false
    },
    approvalInformation: function () {
      if (this.ppaAgreementApprovals && this.ppaAgreementApprovals.length > 0) {
        let latestApproval = this.ppaAgreementApprovals[0]
        let approvedBy = latestApproval.userApprovedBy
        let approvedAt = this.$options.filters.formatDate(latestApproval.approvedAt)
        return 'The vehicle\'s condition was approved by ' + approvedBy + ' (' + approvedAt + ').'
      }
      return ''
    },
    hasWindowStickerFiles: function () {
      if (this.windowStickerFiles && this.windowStickerFiles.length > 0) {
        return true
      }
      return false
    },
    hasWindowStickerPdfFiles: function () {
      if (this.windowStickerPdfFiles && this.windowStickerPdfFiles.length > 0) {
        return true
      }
      return false
    },
    isPpaClosed: function () {
      return this.currentInventory && this.currentInventory.type === 'PpaClosed'
    },
    showCopyAcv: function () {
      return this.isTradePpaIntention && this.currentInventory.proposedCost > 0 && (!this.proposedTradeAllowance || this.proposedTradeAllowance === 0)
    },
    windowStickerStatusRequired: function () {
      return false
    },
    autoCheckValidated: function () {
      if (this.currentDealer && this.currentDealer.autoCheckEnabledStatus) {
        return this.approvePpaAutoCheckIsGood && this.approvePpaAutoCheckCheckComplete
      } else {
        return true
      }
    },
    autoCheckEnabled: function () {
      if (this.currentDealer) {
        return this.currentDealer.autoCheckEnabledStatus
      }
      return false
    },
    mostRecentBlackBookInformation: function () {
      if (this.blackBookInformation) {
        return this.blackBookInformation
      } else {
        if (this.currentInventory && this.currentInventory.blackBook) {
          return this.currentInventory.blackBook
        }
      }
      return null
    },
    blackBookEnabled: function () {
      if (this.currentDealer) {
        return this.currentDealer.blackBookEnabledStatus
      }
      return false
    },
    purchaseCostExceededDealerTolerance: function () {
      return this.callBackAppraisal && (this.callBackAppraisal.badPurchasePricing || this.callBackAppraisal.badAskingPricing)
    },
    purchasedFromNameFallback: function () {
      if (!this.currentInventory) return ''
      if (this.currentInventory.salesPerson && this.currentInventory.salesPerson !== '') return this.currentInventory.salesPerson
      if (this.currentInventory.buyerName && this.currentInventory.buyerName !== '') return this.currentInventory.buyerName
      return ''
    },
    purchasedFromIdFallback: function () {
      if (!this.currentInventory) return 0
      if (this.currentInventory.salesPersonId && this.currentInventory.salesPersonId > 0) return this.currentInventory.salesPersonId
      if (this.currentInventory.buyerId && this.currentInventory.buyerId > 0) return this.currentInventory.buyerId
      return 0
    },
    buildBuyerInfo: function () {
      let buyerInfo = null
      if (!this.currentInventory) return buyerInfo
      if (this.currentInventory.salesPersonId && this.currentInventory.salesPersonId > 0) {
        buyerInfo = {
          mobile: this.currentInventory.salesPersonMobile,
          receiverIdProp: this.currentInventory.salesPersonId,
          email: this.currentInventory.salesPersonEmail,
          name: this.currentInventory.salesPerson,
          telephone: this.currentInventory.salesPersonTelephone,
          ext: this.currentInventory.salesPersonExt,
          trackingNumber: this.currentInventory.salesPersonTrackingNumber
        }
      }
      if (this.currentInventory.buyerId && this.currentInventory.buyerId > 0) {
        buyerInfo = {
          mobile: this.currentInventory.buyerMobile,
          receiverIdProp: this.currentInventory.buyerId,
          email: this.currentInventory.buyerEmail,
          name: this.currentInventory.buyerName,
          telephone: this.currentInventory.buyerTelephone,
          ext: this.currentInventory.buyerExt,
          trackingNumber: this.currentInventory.buyerTrackingNumber
        }
      }

      return buyerInfo
    },
    //The rating handed to the selector's CARFAX match action - see the inventory mixin's copy.
    carfaxRatingForMatch: function () {
      if (this.hasCarfaxTesterRole) return null
      return this.currentCarfaxRating || null
    },
    /*
     * Whether the record agrees with the rating the selector's CARFAX match action compares
     * against: 'matches', 'differs', or 'unknown' when the comparison could not run at all.
     *
     * ⚠ It exists because the two silences are not the same thing. compareCarfaxVehicleInformation
     * leaves the prompt down both when the values agree and when the rating names a make the
     * catalogue does not hold, and reporting the second as a match would tell the operator the
     * record is fine when nothing was checked. The selector reads the same outcome to colour its
     * button, so the words and the colour cannot disagree.
     */
    carfaxMatchState: function () {
      const rating = this.carfaxRatingForMatch
      if (!rating || !rating.make) return 'unknown'
      return carfaxMatchOutcome(buildCarfaxComparison(this.allVehicleMakes, rating, this.selectedMakeModel))
    },
    //See the shared predicate - this was one of two byte-identical copies of it.
    hasCarfaxTesterRole: function () {
      return hasCarfaxTesterRole(this.currentUser)
    },
    isStatusArchived: function () {
      return this.currentInventory && this.currentInventory.status === 'Archived'
    },
    showPpaCloseButton: function () {
      return this.currentInventory && this.isTypePpaAppraisal && !this.isStatusArchived
    },
    hasClosePpaAccess: function () {
      let allowedPermissions = ['inventory.modify.mark-ppaclosed']
      var userHasAccess = false
      allowedPermissions.forEach((x) => {
        userHasAccess = userHasAccess || this.hasFeatureAccess(x)
      })
      return userHasAccess
    }
  },
  methods: {
    onPpaCancelled: function () {
      this.onCancel()
    },
    onSetProposedExpirationDate: function () {
      this.currentInventory.proposedCostExpirationDate = this.defaultProposedExpirationDate
    },
    onRequestApprovePpa: function () {
      this.serverErrorMessage = ''
      this.serverErrors = []
      if (this.currentDealer) {
        this.selectedDealer = {id: this.currentDealer.id, name: this.currentDealer.name}
      }
      if (this.inventoryItem && this.inventoryItem.id) {
        this.isApprovedPpaConfirmationActive = true
        this.loadInventory(this.inventoryItem.id)
      }
    },
    onSave: function () {
      this.serverErrorMessage = ''
      this.serverErrors = []
      this.isSaving = true
      
      let ppaApprovalModel = {
        id: this.currentInventory.id,
        proposedCost: this.currentInventory.proposedCost,
        dispositionIntention: this.selectedDispositionIntentionStatus ? this.selectedDispositionIntentionStatus.name : null,
        dispositionValidDays: +this.currentInventory.dispositionValidDays,
        commitmentFromId: this.selectedCommitmentFrom ? this.selectedCommitmentFrom.id : null,
        firmCommitment: !this.hasCommitment ? null : this.currentInventory.firmCommitment,
        commitmentValue: this.currentInventory.commitmentValue,
        ppaIntention: this.selectedPpaIntention.name,
        cpoStatus: this.isMakeSoldByDealer ? (this.selectedCpoStatus ? this.selectedCpoStatus.name : null) : null,
        conditionType: this.selectedConditionType ? this.selectedConditionType.altName : null,
        ppaPriceAmendmentReason: '',
        ppaAccepted: null,
        ppaExamined: this.currentInventory.ppaExamined,
        inspectionDependent: this.currentInventory.inspectionDependent,
        payoffAmount: this.currentInventory.payoffAmount,
        proposedCostExpirationDate: null,
        proposedTradeAllowance: null,
        usage: this.currentInventory.usage,
        mileageEstimate: this.mileageEstimate,
        trueMileageUnknown: this.trueMileageUnknown,
        mileageExempt: this.mileageExempt,
        attachments: [],
        windowStickerStatus: this.selectedWindowStickerStatus ? this.selectedWindowStickerStatus.name : null,
        serviceInspectionRequest: this.currentInventory.serviceInspectionRequest && !this.currentInventory.hasPpaPreInspection,
        vehicleMakeId: this.selectedMakeModel.makeId < 1 ? null : this.selectedMakeModel.makeId,
        vehicleMakeVersionId: this.selectedMakeModel.modelId < 1 ? null : this.selectedMakeModel.modelId,
        customVehicleMakeVersion: this.selectedMakeModel.customMakeModel,
        transmissionType: this.selectedMakeModel.transmissionType,
        attributes: this.currentInventory.attributes,
        makeModelReconciled: this.currentInventory.makeModelReconciled,
        //⚠ NOT this.selectedMakeModel.notes, which is the free-text SearchNotes string written as an
        //attribute below. This is the inventory note collection, new to the PPA approval model.
        notes: []
      }

      //Records what accepting the CARFAX values replaced - SetVehicleMakeModelInfo and the attribute
      //writes below overwrite the previous make, model, trim, style and year in place, and nothing
      //else keeps them. Empty unless the operator answered Yes to the reconcile prompt.
      if (this.carfaxOverwriteNote) {
        ppaApprovalModel.notes.push(buildCarfaxOverwriteNoteModel(this.carfaxOverwriteNote))
      }

      //Rides the same latch as the note above: set only by an apply that landed on a catalogue
      //model, and sent with the save so an abandoned edit teaches the mapping nothing.
      ppaApprovalModel.carfaxModelMapping = this.carfaxModelMappingRecord

      setInventoryAttribute(ppaApprovalModel, 'ExteriorColor', this.selectedMakeModel.exteriorColor)
      setInventoryAttribute(ppaApprovalModel, 'InteriorColor', this.selectedMakeModel.interiorColor)
      /*
       * ⚠ DELIBERATELY NO DamageNotes WRITE. This line used to read
       * `setInventoryAttribute(ppaApprovalModel, 'DamageNotes', this.damageNote)` - and `this.damageNote`
       * does not exist on this component: not in data, not a computed, not a prop, and defined by none
       * of its three mixins. It was permanently undefined, so every PPA approval marked a damage row
       * Deleted, and line ~1213 passes the LIVE attributes array by reference so the on-screen record
       * was mutated too.
       *
       * This screen has no damage input. Its damage UI is the read-only history table fed by
       * readInventoryAttributeHistory below, so there is nothing here to save. Pinned by
       * damageNotesWriteSurface.spec.js.
       */
      setInventoryAttribute(ppaApprovalModel, 'Style', this.selectedMakeModel.style)
      setInventoryAttribute(ppaApprovalModel, 'Trim', this.selectedMakeModel.trim)
      setInventoryAttribute(ppaApprovalModel, 'ProductionYear', this.selectedMakeModel.productionYear)
      setInventoryAttribute(ppaApprovalModel, 'Make', this.selectedMakeModel.customMakeName)
      setInventoryAttribute(ppaApprovalModel, 'Model', this.selectedMakeModel.customModelName)

      setInventoryAttribute(ppaApprovalModel, 'SearchType', (this.selectedMakeModel.locateType && this.selectedMakeModel.locateType.name) ? this.selectedMakeModel.locateType.name : '')
      setInventoryAttribute(ppaApprovalModel, 'AlternateExteriorColors', (this.selectedMakeModel.altExteriorColors && this.selectedMakeModel.altExteriorColors.length > 0) ? this.selectedMakeModel.altExteriorColors.join(',') : '')
      setInventoryAttribute(ppaApprovalModel, 'AlternateInteriorColors', (this.selectedMakeModel.altInteriorColors && this.selectedMakeModel.altInteriorColors.length > 0) ? this.selectedMakeModel.altInteriorColors.join(',') : '')
      setInventoryAttribute(ppaApprovalModel, 'SearchTags', (this.selectedMakeModel.tags && this.selectedMakeModel.tags.length > 0) ? this.selectedMakeModel.tags.join(',') : '')
      setInventoryAttribute(ppaApprovalModel, 'SearchNotes', (this.selectedMakeModel.notes && this.selectedMakeModel.notes.length > 0) ? this.selectedMakeModel.notes : '')

      if (this.currentInventory.proposedCost && this.currentInventory.proposedCost >= 0) {
        ppaApprovalModel.proposedCostExpirationDate = this.currentInventory.proposedCostExpirationDate
      }
      if (this.selectedPriceAmendmentReason) {
        ppaApprovalModel.ppaPriceAmendmentReason = this.selectedPriceAmendmentReason.altName
      }
      if (this.approvePpaAcceptConditions) {
        ppaApprovalModel.ppaAccepted = this.approvePpaAcceptConditions
      }
      if (this.isTradePpaIntention && this.proposedTradeAllowance !== null) {
        ppaApprovalModel.proposedTradeAllowance = this.proposedTradeAllowance
      }

      if (this.windowStickerPdfFiles.length > 0) {
        var stickerPdfFiles = this.windowStickerPdfFiles.map((x) => {
          return {
            id: x.id && x.id > 0 ? x.id : 0,
            uploadName: x.name,
            uploadSize: x.size,
            uniqueId: x.uniqueId,
            type: x.type,
            classType: 'WindowSticker',
            status: x.status
          }
        })

        ppaApprovalModel.attachments.push(...stickerPdfFiles)
      }
      if (this.windowStickerFiles.length > 0) {
        var stickerFiles = this.windowStickerFiles.map((x) => {
          return {
            id: x.id && x.id > 0 ? x.id : 0,
            uploadName: x.name,
            uploadSize: x.size,
            uniqueId: x.uniqueId,
            type: x.type,
            classType: 'WindowSticker',
            status: x.status
          }
        })

        ppaApprovalModel.attachments.push(...stickerFiles)
      }

      inventoryService.approvePpa(ppaApprovalModel).then(response => {
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.approvedPpaItem = response
          this.approvedSuccessfully = true
          this.approvePpaAutoCheckCheckComplete = true
          this.approvePpaAutoCheckIsGood = true
          this.approvePpaAutoCheckFailed = false
          this.approvePpaCarFaxCheckComplete = false
          this.approvePpaCarFaxIsGood = false
          this.approvePpaCarFaxFailed = false
          this.approvePpaAppraisalCheckComplete = false
          this.approvePpaAppraisalIsGood = false
          this.approvePpaAppraisalFailed = false
          this.approvePpaAcceptConditions = false
          this.blackBookCheckComplete = !this.blackBookEnabled

          // use emit here so that if we in inventory edit screen we dont get prompted with changes alert
          eventBus.$emit('on-changes-cancelled')
          this.onCancel()
        }
        this.isSaving = false
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    onCancel: function () {
      this.$validator.pause()
      if (this.approvedSuccessfully && this.approvedPpaItem && this.reloadList) {
        eventBus.$emit('inventory-changed', [this.approvedPpaItem])
        this.$router.push({name: this.lastActiveListPage !== '' ? this.lastActiveListPage : 'ListInventory'})
        this.isApprovedPpaConfirmationActive = false
        this.reset()
      }

      if (this.approvedSuccessfully && this.approvedPpaItem && !this.reloadList) {
        eventBus.$emit('reload-deal')
        eventBus.$emit('inventory-changed', [this.approvedPpaItem])
        this.isApprovedPpaConfirmationActive = false
        this.reset()
      }

      this.isApprovedPpaConfirmationActive = false
      this.reset()
    },
    reset: function () {
      this.approvedSuccessfully = false
      this.approvedPpaItem = null
      this.serverErrors = []
      this.serverErrorMessage = []
      this.approvePpaAutoCheckCheckComplete = true
      this.approvePpaAutoCheckIsGood = true
      this.approvePpaAutoCheckFailed = false
      this.approvePpaCarFaxCheckComplete = false
      this.approvePpaCarFaxIsGood = false
      this.approvePpaCarFaxFailed = false
      this.approvePpaAppraisalCheckComplete = false
      this.approvePpaAppraisalIsGood = false
      this.approvePpaAppraisalFailed = false
      this.approvePpaAcceptConditions = false
      this.mileageEstimate = false
      this.trueMileageUnknown = false
      this.mileageExempt = false
      this.selectedWindowStickerStatus = null
      this.windowStickerFiles = []
      this.windowStickerPdfFiles = []
      this.blackBookCheckComplete = !this.blackBookEnabled
      this.blackBookInformation = null
      this.selectedDealer = null,
      this.selectedMakeModel = null
      this.currentCarfaxRating = null
      this.carfaxMakeModel = null
      this.showCarfaxModelNameInformation = false
      this.makeModelCheckRequestCompleted = false
    },
    loadInventory (id) {
      this.customerSyncRequired = false
      this.customerSyncRequested = false
      let that = this
      this.isLoading = true
      inventoryService.getForPpaApproval(id).then(response => {        
        that.currentInventory = response.inventory
        that.currentInventory.inspectionDependent = true
        that.selectedPriceAmendmentReason = that.definedTypes.ppaRevisionReasonType.options.find(x => x.altName === that.currentInventory.ppaPriceAmendmentReason)
        that.selectedPpaIntention = that.definedTypes.ppaIntentionType.options.find(x => x.name === that.currentInventory.ppaIntention)
        that.selectedDispositionIntentionStatus = that.definedTypes.dispositionIntentionStatuses.find(x => x.altName === that.currentInventory.dispositionIntention)
        that.mileageEstimate = that.currentInventory.mileageEstimate
        that.trueMileageUnknown = that.currentInventory.trueMileageUnknown
        that.mileageExempt = that.currentInventory.mileageExempt
        if (that.currentInventory.cpoStatus) {
          that.selectedCpoStatus = that.definedTypes.cpoStatusTypes.find(x => x.altName === that.currentInventory.cpoStatus)
        }
        if (that.currentInventory.conditionType) {
          that.selectedConditionType = that.definedTypes.inventoryConditionType.find(x => x.altName === that.currentInventory.conditionType)
        }
        that.onLoadProposedCost = that.currentInventory.proposedCost
        if (!that.currentInventory.proposedCostExpirationDate) {
          that.currentInventory.proposedCostExpirationDate = that.defaultProposedExpirationDate
        }
        that.proposedTradeAllowance = that.currentInventory.proposedTradeAllowance
        if (that.currentInventory.commitmentFromId > 0) {
          that.selectedCommitmentFrom = {
            id: that.currentInventory.commitmentFromId,
            name: that.currentInventory.commitmentFromName
          }
        }
        that.setInitialProposedCost()
        that.selectedWindowStickerStatus = that.definedTypes.windowStickerStatusOptions.find((x) => (x.name === that.currentInventory.windowStickerStatus || x.altName === that.currentInventory.windowStickerStatus))
        if (that.currentInventory.lockWindowSticker) {
          that.windowStickerDisabled = true
        }
        that.currentInventory.attachments.forEach((x) => {
          x.loaded = true
          x.imageData = x.fileLocation
          x.fileType = that.getFileType(x.fileLocation)

          if (x.type !== 'Pdf' && x.classType === 'WindowSticker' && x.status === 'Active') {
            that.windowStickerFiles.push(x)
          }
          if (x.type === 'Pdf') {
            if (x.classType === 'WindowSticker' && x.status === 'Active') {
              that.windowStickerPdfFiles.push(x)
            }
          }
        })
        if (that.currentInventory && that.currentInventory.attributes) {
          that.damageHistory = that.currentInventory.attributes.filter((x) => x.name === 'DamageNotes' && x.value)
        }
        if (that.damageHistory && that.damageHistory.length > 0) {
          that.isDamageContainerCollapsed = false
        }  
        
        that.selectedTypeStatus = that.definedTypes.inventoryTypes.find((x) => (x.name === that.currentInventory.type || x.altName === that.currentInventory.type))
        that.isLoading = false
        that.$nextTick(() => {
          that.setSelectedMakeModel()
        })
        that.currentCarfaxRating = that.currentInventory.carFaxRating
      }).catch((error) => {
        that.serverErrorMessage = error.message
      })
    },
    setInitialProposedCostAsCurrent: function () {
      this.currentInventory.proposedCost = this.initialProposedCost
    },
    setInitialProposedCost: function () {
      if (this.currentInventory && this.currentInventory.proposedCosts && this.currentInventory.proposedCosts.length > 0) {
        let lastPosition = this.currentInventory.proposedCosts.length - 1
        this.initialProposedCost = this.currentInventory.proposedCosts[lastPosition].proposedCost
      }
    },
    resetProposedCostAsCurrent: function () {
      this.currentInventory.proposedCost = this.onLoadProposedCost
    },
    onAutoCheckForApprovePpa: function (autoCheckRating) {
      this.approvePpaAutoCheckCheckComplete = true
      this.approvePpaAutoCheckIsGood = autoCheckRating && !((autoCheckRating.accidentCount && autoCheckRating.accidentCount > 0) || (autoCheckRating.recallCount && autoCheckRating.recallCount > 0))
      if (this.inventoryItem && this.inventoryItem.autoCheckRating && this.inventoryItem.autoCheckRating.hashValue && autoCheckRating && autoCheckRating.hashValue) {
        this.autoCheckHashMatch = (this.inventoryItem.autoCheckRating.hashValue === autoCheckRating.hashValue)
      } else {
        this.autoCheckHashMatch = false
      }
      this.approvePpaAutoCheckFailed = !autoCheckRating
      if (this.autoCheckHashMatch === true && this.hasAutoCheckRatingApproval === true) {
        this.approvePpaAutoCheckIsGood = true
        this.autoCheckApproved = true
        this.approvePpaAutoCheckFailed = false
      } else if (this.inventoryItem && !autoCheckRating && this.inventoryItem.hasRatingApproval === true) {
        this.approvePpaAutoCheckIsGood = true
        this.autoCheckApproved = true
        this.approvePpaAutoCheckFailed = false
      }
    },
    onShowAutoCheckReport: function () {
      this.isApprovedPpaConfirmationActive = false
      this.showReport = true
    },
    onHideAutoCheckReport: function () {
      this.showReport = false
      this.isApprovedPpaConfirmationActive = true
    },
    onCarFaxForApprovePpa: function (carFaxRating) {
      this.carfaxRequested = false
      this.approvePpaCarFaxCheckComplete = true
      this.approvePpaCarFaxIsGood = carFaxRating && !(carFaxRating.brandedTitle === true || (carFaxRating.totalAccidents && carFaxRating.totalAccidents > 0) || carFaxRating.accidentIndicator === true || carFaxRating.frameDamageOrRepair === true || carFaxRating.airBagDeployed === true || carFaxRating.damaged === true || carFaxRating.majorProblems === true || carFaxRating.recall === true)

      if (this.inventoryItem && this.inventoryItem.carFaxRating && this.inventoryItem.carFaxRating.hashValue && carFaxRating && carFaxRating.hashValue) {
        this.carFaxHashMatch = (this.inventoryItem.carFaxRating.hashValue === carFaxRating.hashValue)
      } else {
        this.carFaxHashMatch = false
        if (carFaxRating) {
          this.currentCarfaxRating = carFaxRating
        }
      }
      this.approvePpaCarFaxFailed = !carFaxRating
      if (this.carFaxHashMatch === true && this.hasCarFaxRatingApproval === true) {
        this.approvePpaCarFaxIsGood = true
        this.carFaxApproved = true
        this.approvePpaCarFaxFailed = false
      } else if (this.inventoryItem && (!this.inventoryItem.carFaxRating || !carFaxRating) && this.inventoryItem.hasRatingApproval === true) {
        this.approvePpaCarFaxIsGood = true
        this.carFaxApproved = true
        this.approvePpaCarFaxFailed = false
      }
    },
    /*
     * ⚠ This screen binds on-new-carfax to its OWN handler, so the comparison never saw the event -
     * it was reached only through on-carfax-vehicle-information, which CarFaxControl emits with no
     * arguments at all. So the refresh could not carry "the operator asked for this" and the box
     * could not be raised again here, which is the screen Garrett was actually on.
     *
     * ⚠ Order is load-bearing: onCarFaxForApprovePpa is what writes currentCarfaxRating, and the
     * comparison below reads it.
     */
    onApprovePpaNewCarfax: function (carFaxRating, context) {
      this.onCarFaxForApprovePpa(carFaxRating)
      this.onCarFaxVehicleInformation(carFaxRating, context)
    },
    onShowCarFaxReport: function () {
      this.isApprovedPpaConfirmationActive = false
      this.showCarFaxReport = true
    },
    onHideCarFaxReport: function () {
      this.showCarFaxReport = false
      this.isApprovedPpaConfirmationActive = true
    },
    onAppraised: function (appraisal) {
      this.callBackAppraisal = null
      this.callBackAppraisal = appraisal
      this.approvePpaAppraisalCheckComplete = true
      if (appraisal && appraisal !== '') {
        this.approvePpaAppraisalIsGood = appraisal && appraisal.isGood
        this.currentInventory.appraisals.unshift(appraisal)
        if (this.inventoryItem && this.inventoryItem.appraisalRating && this.inventoryItem.appraisalRating.hashValue && appraisal && appraisal.hashValue) {
          this.appraisalRatingHashMatch = (this.inventoryItem.appraisalRating.hashValue === appraisal.hashValue)
        } else {
          this.appraisalRatingHashMatch = false
        }
        if (this.appraisalRatingHashMatch === true && this.hasAppraisalRatingApproval === true) {
          this.approvePpaAppraisalIsGood = true
          this.appraisalApproved = true
          this.approvePpaAppraisalFailed = false
        } else if (this.inventoryItem && !this.inventoryItem.appraisalRating && this.inventoryItem.hasRatingApproval === true) {
          this.approvePpaAppraisalIsGood = true
          this.appraisalApproved = true
          this.approvePpaAppraisalFailed = false
        }
      } else {
        this.approvePpaAppraisalFailed = !appraisal
        if (this.inventoryItem && this.inventoryItem.hasRatingApproval === true) {
          this.approvePpaAppraisalIsGood = true
          this.appraisalApproved = true
          this.approvePpaAppraisalFailed = false
        }
      }
    },
    wholesaleDayAdded: function (newItem) {
      this.currentInventory.dispositionValidDays = newItem
    },
    confirmTradeAllowanceChange: function () {
      let that = this
      that.$nextTick(() => {
        that.proposedTradeAllowance = that.currentInventory.proposedCost
        that.confirmTradeAllowanceChangeActive = false
      })
    },
    onProposedCostChange: function () {
      if (this.isTradePpaIntention && this.proposedCostChanged) {
        if (this.proposedTradeAllowance && this.proposedTradeAllowance > 0) {
          this.confirmTradeAllowanceChangeActive = false
        }
      }
    },
    onCopyInfo: function (text) {
      let that = this
      this.$copyText(text).then(function (e) {
        that.$buefy.toast.open(
          {
            duration: 2500,
            message: text + ' copied to clipboard',
            position: 'is-top',
            type: 'is-info'
          })
      }, function (e) {
        that.$buefy.toast.open(
          {
            duration: 2500,
            message: 'Could not copy info to clipboard',
            position: 'is-top',
            type: 'is-warning'
          })
      })
    },
    onStockNoClick: function (inventoryItem) {
      let that = this

      if (inventoryItem && inventoryItem.stockNo !== '') {
        this.$copyText(inventoryItem.stockNo).then(
          function (e) {
            that.$buefy.toast.open({
              duration: 2500,
              message: 'Stock No. ' + inventoryItem.stockNo + ' copied to clipboard',
              position: 'is-top',
              type: 'is-info'
            })
          }, function (e) {
            that.$buefy.toast.open({
              duration: 2500,
              message: 'Could not copy stock no. to clipboard',
              position: 'is-top',
              type: 'is-warning'
            })
          }
        )
      }
    },
    onCopyProposedCostToTradeAllowance: function () {
      this.proposedTradeAllowance = this.currentInventory.proposedCost
    },
    uploadStarted: function () {
      this.fileUploadCount++
      this.isUploadingFile = true
    },
    uploadComplete: function () {
      this.fileUploadCount--
      if (this.fileUploadCount === 0) {
        this.isUploadingFile = false
      }
    },
    onAutoCheckDisabled: function () {
      this.approvePpaAutoCheckCheckComplete = true
    },
    onBlackBookInformationRetrieved: function (blackBook) {
      this.blackBookCheckComplete = true
      this.blackBookInformation = blackBook
      this.blackBookCheckFailed = !blackBook
      if (blackBook && blackBook.vinNo && blackBook.vinNo.length > 0) {
        this.blackBookCheckIsGood = true
      } else {
        this.blackBookCheckIsGood = false
      }
    },
    onBlackBookDisabled: function () {
      this.blackBookCheckComplete = true
      this.blackBookCheckIsGood = true
      this.blackBookCheckFailed = false
    },
    onRestoreMakeModel: function () {
      console.log('Restoring original make model selection')
      this.selectedMakeModel = cloneDeep(this.originalMakeModel)
    },
    setSelectedMakeModel: function () {
      var exteriorColor = this.currentInventory.attributes.find((x) => x.name === 'ExteriorColor')
      var interiorColor = this.currentInventory.attributes.find((x) => x.name === 'InteriorColor')
      var vehicleTrim = this.currentInventory.attributes.find((x) => x.name === 'Trim')
      var vehicleStyle = this.currentInventory.attributes.find((x) => x.name === 'Style')
      var searchType = this.currentInventory.attributes.find((x) => x.name === 'SearchType')
      var altExteriorColors = this.currentInventory.attributes.find((x) => x.name === 'AlternateExteriorColors')
      var altInteriorColors = this.currentInventory.attributes.find((x) => x.name === 'AlternateInteriorColors')
      var searchTags = this.currentInventory.attributes.find((x) => x.name === 'SearchTags')
      var searchNotes = this.currentInventory.attributes.find((x) => x.name === 'SearchNotes')

      var vehicleAge = moment().year() - (+this.currentInventory.vehicleProductionYear)
      var cpoAllowed = true
      var autoCpoStatus = 'Ineligible'
      if (vehicleAge >= 0 && vehicleAge <= this.selectedDealer.autoCpoAgeLimit) {
        cpoAllowed = true
        autoCpoStatus = 'Cpo'
      } else if (vehicleAge > this.selectedDealer.autoCpoAgeLimit && vehicleAge <= this.selectedDealer.cpoQualifyingAge) {
        cpoAllowed = true
        autoCpoStatus = 'Eligible'
      }

      let selectedMake = this.currentInventory.vehicleMakeId > 0 ? this.allVehicleMakes.find((x) => x.id === this.currentInventory.vehicleMakeId) : null
      let selectedModel = selectedMake && selectedMake.versions ? (selectedMake.versions.find((x) => x.id === this.currentInventory.vehicleMakeVersionId)) : null

      this.selectedMakeModel = {
        exteriorColor: exteriorColor ? exteriorColor.value : '',
        interiorColor: interiorColor ? interiorColor.value : '',
        trim: vehicleTrim ? vehicleTrim.value : '',
        style: vehicleStyle ? vehicleStyle.value : '',
        makeId: this.currentInventory.vehicleMakeId,
        selectedMake: selectedMake,
        modelId: this.currentInventory.vehicleMakeVersionId,
        selectedModel: selectedModel,
        productionYear: this.currentInventory.vehicleProductionYear,
        customMakeName: this.currentInventory.customMake === '' ? null : this.currentInventory.customMake,
        customModelName: this.currentInventory.customModel === '' ? null : this.currentInventory.customModel,
        customMakeModel: this.currentInventory.customVehicleMakeVersion,
        vehicleAge: vehicleAge,
        cpoAllowed: cpoAllowed,
        autoCpoStatus: autoCpoStatus,
        locateType: (searchType && searchType.value && searchType.value !== '') ? this.definedTypes.allowedLocateTypes.options.find((x) => x.name === searchType.value) : null,
        altExteriorColors: (altExteriorColors && altExteriorColors.value && altExteriorColors.value.length > 0) ? altExteriorColors.value.split(',') : [],
        altInteriorColors: (altInteriorColors && altInteriorColors.value && altInteriorColors.value.length > 0) ? altInteriorColors.value.split(',') : [],
        tags: (searchTags && searchTags.value && searchTags.value.length > 0) ? searchTags.value.split(',') : [],
        notes: searchNotes ? searchNotes.value : '',
        transmissionType: this.currentInventory.transmissionType
      }

      // Make a backup to restore from make model selector
      this.originalMakeModel = cloneDeep(this.selectedMakeModel)
    },
    //See the inventory mixin's copy for why a forced path exists and why forcing is safe.
    compareCarfaxVehicleInformation: function (options) {
      const force = !!(options && options.force === true)
      let makeModelReconciled = this.currentInventory ? this.currentInventory.makeModelReconciled : false
      if (!this.hasCarfaxTesterRole && this.currentCarfaxRating && (force || makeModelReconciled === false)) {
        this.carfaxCustomMakeModelName = ''

        //Shared with the inventory and roServiceVehicle mixins. buildCarfaxComparison is this
        //orchestration extracted from the three byte-identical copies that were being kept in step by
        //hand - the quick-view needed a fourth, which is the threshold, not the first case.
        const comparison = buildCarfaxComparison(this.allVehicleMakes, this.currentCarfaxRating, this.selectedMakeModel)
        this.carfaxMakeModel = comparison.carfaxMakeModel
        //⚠ The five flags are ALWAYS reset by the helper, which the inline version did not do - it
        //reset them inside its own `if (make)`, so a rating naming no make left the previous run's
        //flags standing. Latent while the prompt fired once; reachable the moment repeat runs exist.
        this.vehicleMakeDifferent = comparison.differences.vehicleMakeDifferent
        this.modelNameDifferent = comparison.differences.modelNameDifferent
        this.productionYearDifferent = comparison.differences.productionYearDifferent
        this.trimDifferent = comparison.differences.trimDifferent
        this.styleDifferent = comparison.differences.styleDifferent

        //⚠ ONE reason to interrupt: carfax names a different model than the record holds. This used
        //to raise on any of the five flags, which put the prompt on screen for VIN ZFF83CLA4K0248000
        //over a trim of "-" against "Superfast" and a style of "-" against "Coupe" - a modal quoting
        //two identical model names at the operator. The other four fields are still reported, by the
        //match icon and the field-by-field table behind it; they just no longer demand an answer.
        if (carfaxModelNameDiffers(comparison)) {
          if (this.carfaxMakeModel) {
            //⚠ A pointer, not a description. Both branches used to inline the two full vehicle
            //descriptions into one sentence, so the operator had to diff two prose strings to find the
            //single field that had moved - Garrett, on PPA108227: "I can't even understand this, it's
            //almost too much blah blah blah." The model choices are now listed by
            //CarfaxModelReconcile, and the free-text and resolved cases read the same because the
            //table handles both.
            this.carfaxModelNameInformation = buildReconcileMessage()
            this.showCarfaxModelNameInformation = true
            this.makeModelCheckRequestCompleted = true
          }
        }
      }
    },
    //⚠ Delegates - the body moved to carfax_make_model_resolution.buildCarfaxCustomMakeModelValues.
    buildCarfaxCustomMakeModel: function (make, productionYear, model, trim, style) {
      Object.assign(this.carfaxMakeModel, buildCarfaxCustomMakeModelValues(make, productionYear, model, trim, style))
    },
    /*
     * The reconcile prompt's Apply, which hands over the row the operator TICKED.
     *
     * ⚠ Not always CARFAX's answer. CARFAX returns the model and the series as separate fields -
     * "812" and "Superfast" - while the catalogue row is named "812 Superfast", so CARFAX's own value
     * is frequently the one thing that cannot be stored properly. The table offers the catalogue rows
     * behind it and this passes the chosen one down; the current row is pre-checked, so applying
     * without touching the table changes no model.
     *
     * ⚠ This screen keeps its own copy rather than using the inventory mixin's - it does not mix that
     * in. Keep the two in step.
     */
    onCarfaxModelReconcileApply: function (chosenRow) {
      this.showCarfaxDecodedVehicleDetails(chosenRow && chosenRow.model)
    },
    showCarfaxDecodedVehicleDetails: function (chosenModel) {
      this.showCarfaxModelNameInformation = false
      
      let that = this
      that.$nextTick(() => {
        const refs = this.$refs
        const makeModelSelectorComponent = this.$refs.makeModelSelectorRef
        if (makeModelSelectorComponent) {
          //⚠ Snapshot BEFORE the apply, and as flat COPIES. applyCarfaxMakeModel opens by nulling the
          //year, model, trim and style, and selectedMakeModel is the selector's own object rather than
          //a copy of it - so a moment later there is nothing left to read. Nothing else records these:
          //the approval rewrites the InventoryAttribute row in place, that row has no previous-value
          //column, and AuditTrailListener has the old state in hand and never reads it. Garrett, on
          //this very screen: "This is the data that was there. How do I remember what that was?"
          //(31 Aug, change 5.)
          const before = readMakeModelFields(this.selectedMakeModel)

          //⚠ APPLIES the carfax values into the form now, rather than decorating the fields with chips the
          //operator then has to click one by one. Garrett's change 3: "nothing here gives them a place to
          //actually select that selection. So they're leaving it like this. That car does not exist as a
          //296." It falls back to the old suggest-only behaviour by itself when carfax's model resolved
          //to no catalogue row, which is the common failure rather than an edge case.
          makeModelSelectorComponent.applyCarfaxMakeModel(this.carfaxMakeModel, chosenModel)

          //Held until the approval payload is built rather than posted now, so answering Yes and then
          //abandoning the approval writes nothing - which is right, because nothing was persisted.
          //⚠ The vehicle as it stood BEFORE this apply - Inventory.Name is a server-composed DTO string
          //fixed when the record loaded, and nothing here writes it.
          that.carfaxOverwriteNote = buildCarfaxOverwriteNote(before, readMakeModelFields(this.selectedMakeModel), {
            make: this.vehicleMakeDifferent,
            productionYear: this.productionYearDifferent,
            model: this.modelNameDifferent,
            trim: this.trimDifferent,
            style: this.styleDifferent
          }, this.currentInventory && this.currentInventory.name)

          that.inventoryMarkMakeModelReconciled()
        }
      })
    },
    readyForMakeModelComparisonCheck() {
      if (!this.hasCarfaxTesterRole && this.approvePpaAppraisalCheckComplete && this.approvePpaCarFaxCheckComplete && this.approvePpaAutoCheckCheckComplete && this.blackBookCheckComplete && this.makeModelCheckRequestCompleted === false) {
        this.compareCarfaxVehicleInformation()
      }
    },
    /*
     * ⚠ `context.userRequested` marks a refresh the OPERATOR asked for, and only that raises the box
     * again. Without the distinction it cannot be reopened at all: the one-shot below blocks a second
     * prompt per visit, and makeModelReconciled blocks it for good.
     *
     * ⚠ Still reached with NO arguments from on-carfax-vehicle-information, which CarFaxControl emits
     * bare after it stores a rating - so both the rating and the context fall back, and that path
     * keeps behaving exactly as it did.
     */
    onCarFaxVehicleInformation: function (carfaxRating, context) {
      const userRequested = !!(context && context.userRequested === true)
      const rating = carfaxRating || this.currentCarfaxRating
      if (this.hasCarfaxTesterRole || !rating || !rating.make) return
      if (!userRequested && this.makeModelCheckRequestCompleted !== false) return
      this.compareCarfaxVehicleInformation({ force: userRequested })
    },
    onCarFaxRequested: function () {
      this.carfaxRequested = true
    },
    setLeadStatusValue: function (crmModel) {
      if (crmModel.status && crmModel.status !== '-') {
        this.canShowCustomerSync()
        this.leadStatusValue = crmModel.status
      }
      if (crmModel.trade && crmModel.trade !== '-') {
        this.leadTradeValue = crmModel.trade
      }
    },
    /*
     * ⚠ "No" DISMISSES, it no longer reconciles. It used to call inventoryMarkMakeModelReconciled -
     * an immediate server write - so a single dismissal locked the box out of this record for good,
     * and Garrett's vehicles are PPAs. Fifteen active records are latched that way today and twelve of
     * them still disagree with their CARFAX rating.
     *
     * ⚠ Yes still reconciles (showCarfaxDecodedVehicleDetails) - accepting the values genuinely does
     * settle the record. Only declining stops being permanent. Matches EditInventory/NewInventory,
     * whose No handler has never persisted anything.
     *
     * Accepted cost: it will prompt again on the next approval until the values agree or Yes is used.
     */
    closeMakeModelNameInformationModal: function () {
      this.showCarfaxModelNameInformation = false
      //⚠ Both flags - the selector raises one dismiss event for either view.
      this.showCarfaxFieldDifferences = false
      if (this.customerSyncRequired === true) {
        this.customerSyncRequested = true
      }
    },
    markMakeModelReconciled: function () {
      if (this.currentInventory && this.currentInventory.makeModelReconciled === false) {
        this.currentInventory.makeModelReconciled = true
      }
    },
    //The selector's CARFAX match action - see the inventory mixin's copy, which carries the reasoning.
    requestCarfaxMakeModelCheck: function () {
      this.showCarfaxModelNameInformation = false
      this.showCarfaxFieldDifferences = false
      this.compareCarfaxVehicleInformation({ force: true })
      if (this.showCarfaxModelNameInformation === true) return

      //⚠ THREE outcomes, not two. The prompt raises on the model name alone, so "did not raise" no
      //longer means the comparison could not run - it also covers a model that agrees while trim,
      //style or year differs. That case gets the read-only comparison rather than a toast claiming
      //the make is missing from the catalogue.
      if (this.carfaxMatchState === 'differs') {
        this.showCarfaxFieldDifferences = true
      } else if (this.carfaxMatchState === 'matches') {
        this.$buefy.toast.open({ message: 'The vehicle information already matches the stored CARFAX report.', type: 'is-info', duration: 5000, position: 'is-bottom' })
      } else {
        this.$buefy.toast.open({ message: 'This vehicle could not be compared against the CARFAX report. The make on the report is not in the vehicle list.', type: 'is-warning', duration: 5000, position: 'is-bottom' })
      }
    },
    inventoryMarkMakeModelReconciled: function () {
      if (this.currentInventory && this.currentInventory.id && this.currentInventory.id > 0) {
        this.isSaving = true
        this.serverErrorMessage = ''
        this.serverErrors = []
        let that = this
        inventoryService.markMakeModelReconciled(that.currentInventory.id).then(response => {
          that.isSaving = false
          if (response.errors) {
            that.serverErrors = response.errors
            console.warn("Could not mark make and model information reconciled.");
          } else {
            console.log('Vehicle make and model information successfully reconciled.')
          }
        }).catch((error) => {
          that.serverErrorMessage = error.message
          that.isSaving = false
        })
      }
    },
    onCarfaxPaymentRequired: function () {
      this.hideCarfaxStatusHeader = true
    },
    onCarfaxPaymentRequested: function () {
      this.hideCarfaxStatusHeader = false
    },
    canShowCustomerSync: function () {
      if (this.closePpaRequestConfirmed === false) {
          if (this.confirmTradeAllowanceChangeActive === true || this.showCarfaxModelNameInformation === true) {
            this.customerSyncRequired = true
          } else {
            let control = this.$refs['closePpaAction']
            this.customerSyncRequested = true
          }
      } else {
        this.closePpaRequestConfirmed = false
        this.$nextTick(() => {
          let control = this.$refs['closePpaAction']
          if (control && typeof control.onRequestClosePpa === 'function') {
            control.onRequestClosePpa()
          }
        })
      }
    },
    closeTradeAllowanceModal: function() {
      this.confirmTradeAllowanceChangeActive = false
      if (this.customerSyncRequired === true) {
        this.customerSyncRequested = true
      }
    },
    showClosePpaControl: function () {
      this.inactivePpaRejectActive = false
      this.closePpaRequestConfirmed = true
    }
  },
  watch: {
    inventoryItem: function (newValue, oldValue) {
      if (newValue) {
        this.selectedPpaIntention = this.definedTypes.ppaIntentionType.options.find(x => x.name === newValue.ppaIntention)
        this.proposedTradeAllowance = newValue.proposedTradeAllowance
      } else {
        this.selectedPpaIntention = null
        this.proposedTradeAllowance = null
      }
    },
    inventoryItemId: function (newValue, oldValue) {
      if (oldValue && newValue) {
        this.reset()
      }
    },
    selectedDispositionIntentionStatus: function (newValue, oldValue) {
      if (this.currentInventory) {
        let originalDispositionIntention = this.currentInventory.dispositionIntention
        if (originalDispositionIntention === newValue) {
          if (newValue && (newValue.id === 2 || newValue.id === 3)) {
            if (this.currentInventory.commitmentFromId !== null && this.currentInventory.commitmentFromId > 0) {
              this.hasCommitment = true
              this.selectedCommitmentFrom = {
                id: this.currentInventory.commitmentFromId,
                name: this.currentInventory.commitmentFromName
              }
            } else {
              this.hasCommitment = false
            }
          }
        } else {
          this.hasCommitment = false
          this.currentInventory.firmCommitment = null
          this.selectedCommitmentFrom = null
          this.currentInventory.dispositionValidDays = null
        }
      }
    },
    mileageEstimate: function (newValue, oldValue) {
      if (newValue === false) {
        this.trueMileageUnknown = false
        this.mileageExempt = false
      }
    },
    mostRecentAppraisal: function (newValue, oldValue) {
      if (newValue) {
        this.onLoadEstWholesaleValue = newValue.estWholesaleValue
      }
    },
    approvePpaAppraisalCheckComplete: function (newValue, oldValue) {
      if (newValue && newValue === true) {
        this.readyForMakeModelComparisonCheck()
      }
    },
    approvePpaCarFaxCheckComplete: function (newValue, oldValue) {
      if (newValue && newValue === true) {
        this.readyForMakeModelComparisonCheck()
      }
    },
    approvePpaAutoCheckCheckComplete: function (newValue, oldValue) {
      if (newValue && newValue === true) {
        this.readyForMakeModelComparisonCheck()
      }
    },
    blackBookCheckComplete: function (newValue, oldValue) {
      if (newValue && newValue === true) {
        this.readyForMakeModelComparisonCheck()
      }
    }
  },
  mounted () {
    this.selectedPpaIntention = this.definedTypes.ppaIntentionType.options.find(x => x.name === this.inventoryItem.ppaIntention)
    this.proposedTradeAllowance = this.inventoryItem.proposedTradeAllowance
    this.$emit('on-mounted-callback')
    if (!this.currentDealer || !this.currentDealer.blackBookEnabled) {
      this.blackBookCheckComplete = true
    }
  },
  created () {
    // eventBus.$on('parent-make-model-onSave', this.markMakeModelReconciled)
    // eventBus.$on('parent-make-model-onSave-book-deal-capture', this.markMakeModelReconciled)    
  },
  destroyed () {
    // eventBus.$off('parent-make-model-onSave', this.markMakeModelReconciled)
    // eventBus.$off('parent-make-model-onSave-book-deal-capture', this.markMakeModelReconciled)
  }
}

</script>

<style scoped>
  .content ul{
    list-style-type: none;
    margin-top: 0
  }

  .modal-card-head, .modal-card-foot{
    padding: 20px !important
  }

  .vauto-status-container {
    margin-top: 20px;
    padding: 0;
    margin-left: 0;
    margin-right: 0;
  }

  .vauto-container-display-only {
    display: inline-flex;
    flex-flow: row;
    align-items: center;
    margin-top:10px;
  }

  .modal-fullscreen .modal-card {
    max-width: 100%;
    margin: 0;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    position: fixed;
    z-index: 100000;
    max-height: 100% !important;
  }

  .modal-card-body {
    overflow:auto;
    height: calc(110vh - 350px);
  }

</style>
